<template>
    <HomeLayout>

        <body class="care-register">
            <LoadingSpinner :isLoading="isLoading" />
            <div class="view">
                <ToastManagerVue ref="toastManager" />
                <div class="page-hero">
                    <div class="hero-tag">Care Giver Registration</div>
                    <div class="hero-title">Start your verified <em>care career</em></div>
                    <div class="hero-sub">Register on Succour, get your Care ID, and connect with families who need you.
                    </div>
                </div>

                <div class="page-wrap" id="reg-form-wrap">
                    <div class="progress-bar" id="reg-progress">
                        <div class="pb-step" :class="{ active: currentStep >= 1 }">
                            <div class="pb-num">1</div>
                            <div class="pb-lbl">Personal Details</div>
                        </div>
                        <div class="pb-step" :class="{ active: currentStep >= 2 }">
                            <div class="pb-num">2</div>
                            <div class="pb-lbl">Experience</div>
                        </div>
                        <div class="pb-step" :class="{ active: currentStep >= 3 }">
                            <div class="pb-num">3</div>
                            <div class="pb-lbl">Documents</div>
                        </div>
                        <div class="pb-step" :class="{ active: currentStep >= 4 }">
                            <div class="pb-num">4</div>
                            <div class="pb-lbl">Review & Submit</div>
                        </div>
                    </div>

                    <!-- ── STEP 1: PERSONAL DETAILS ── -->
                    <div id="reg-step-1" v-show="currentStep === 1">
                        <!-- Profile Photo (compulsory) -->
                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">📸</div> Profile Photo <span
                                    style="color:var(--red);font-size:11px;margin-left:6px;">* Required</span>
                            </div>
                            <div class="photo-upload">
                                <div class="photo-preview" @click="$refs.photoInput.click()">
                                    <template v-if="photoDataURL">
                                        <img :src="photoDataURL" id="photo-img" />
                                    </template>

                                    <template v-else>
                                        📷
                                    </template>
                                </div>

                                <div>
                                    <button type="button" class="photo-upload-btn" @click="$refs.photoInput.click()">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>

                                        Upload Photo
                                    </button>

                                    <div class="photo-upload-hint">
                                        Clear passport-style photo · JPG or PNG · Max 2MB
                                    </div>

                                    <div v-if="errors.photo" class="field-error">
                                        {{ errors.photo }}
                                    </div>

                                    <input ref="photoInput" type="file" accept="image/*" @change="previewPhoto"
                                        style="display:none" />
                                </div>
                            </div>
                        </div>

                        <!-- Personal Information -->
                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">👤</div> Personal Information
                            </div>
                            <div class="form-grid">
                                <div class="field">
                                    <label>First Name <span class="req">*</span></label>
                                    <input type="text" id="r-firstname" v-model="form.firstname"
                                        placeholder="e.g. Amaka" @input="clearError('firstname')" />
                                    <div v-if="errors.firstname" class="field-error">
                                        {{ errors.firstname }}
                                    </div>
                                </div>
                                <div class="field">
                                    <label>Last Name <span class="req">*</span></label>
                                    <input type="text" id="r-lastname" v-model="form.lastname"
                                        placeholder="e.g. Okonkwo" @input="clearError('lastname')" />
                                    <div v-if="errors.lastname" class="field-error">
                                        {{ errors.lastname }}
                                    </div>
                                </div>

                            </div>
                            <div class="form-grid">
                                <div class="field">
                                    <label>
                                        Date of Birth <span class="req">*</span>
                                    </label>

                                    <input type="date" id="r-dob" v-model="form.dob" @input="clearError('dob')"
                                        :max="maxDOB" />

                                    <div v-if="errors.dob" class="field-error">
                                        {{ errors.dob }}
                                    </div>
                                </div>
                                <div class="field"><label>Gender <span class="req">*</span></label>
                                    <select id="r-gender" v-model="form.gender" @change="clearError('gender')">
                                        <option value="">— Select —</option>
                                        <option value="female">Female</option>
                                        <option value="male">Male</option>
                                    </select>
                                    <div v-if="errors.gender" class="field-error">
                                        {{ errors.gender }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-grid">
                                <div class="field"><label>Phone Number <span class="req">*</span></label><input
                                        type="tel" id="r-phone" v-model="form.phone" placeholder="0800 000 0000"
                                        @input="onPhoneInput" />
                                    <div v-if="errors.phone" class="field-error">
                                        {{ errors.phone }}
                                    </div>
                                </div>
                                <div class="field">

                                    <label>
                                        WhatsApp Number <span class="req">*</span>
                                    </label>

                                    <div class="phone-row">

                                        <input type="tel" id="r-whatsapp" v-model="form.whatsapp"
                                            placeholder="Enter WhatsApp number" @input="onWhatsappInput" />

                                        <label class="same-check">
                                            <input type="checkbox" v-model="sameAsPhone" @change="toggleSameWhatsapp" />

                                            Same as phone
                                        </label>

                                    </div>

                                    <div v-if="errors.whatsapp" class="field-error">
                                        {{ errors.whatsapp }}
                                    </div>

                                </div>
                            </div>
                            <div class="form-grid">
                                <div class="field"><label>Email Address <span class="req">*</span></label><input
                                        type="email" id="r-email" v-model="form.email" placeholder="amaka@email.com"
                                        @input="clearError('email')" autocomplete="email" inputmode="email" />
                                    <div v-if="emailVerified" class="email-verified" id="email-verified">
                                        <svg viewBox="0 0 24 24">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        Email verified
                                    </div>
                                    <div v-if="errors.email" class="field-error">
                                        {{ errors.email }}
                                    </div>
                                </div>
                                <div class="field">
                                    <label>Current State of Residence <span class="req">*</span></label>
                                    <select id="r-state" v-model="form.state" @change="clearError('state')">
                                        <option value="">— Select State —</option>
                                        <option v-for="state in states" :key="state.alias" :value="state.state">
                                            {{ state.state }}
                                        </option>
                                    </select>

                                    <div v-if="errors.state" class="field-error">
                                        {{ errors.state }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-grid-1">
                                <div class="field">
                                    <label>Willingness to Relocate <span class="req">*</span></label>
                                    <select id="r-relocate" v-model="form.relocate" @change="clearError('relocate')">
                                        <option value="">— Select —</option>
                                        <option value="lagos">Yes — Lagos</option>
                                        <option value="abuja">Yes — Abuja</option>
                                        <option value="portharcourt">Yes — Port Harcourt</option>
                                        <option value="any_location">Yes — Any location</option>
                                        <option value="current_only">No — current location only</option>
                                    </select>
                                    <div v-if="errors.relocate" class="field-error">
                                        {{ errors.relocate }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Consent -->
                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">📋</div> Consent & Declaration
                            </div>
                            <div class="consent-box" id="consent-box" :style="{
                                borderColor: form.consent
                                    ? 'var(--green)'
                                    : 'var(--border)'
                            }">
                                <div class="consent-inner">
                                    <input type="checkbox" id="r-consent" v-model="form.consent"
                                        @change="clearError('consent')" />
                                    <div v-if="errors.consent" class="field-error">
                                        {{ errors.consent }}
                                    </div>
                                    <div class="consent-text">
                                        I, the applicant, hereby consent to <strong>Succour</strong> collecting, storing
                                        and
                                        using my personal data — including identity details, employment history,
                                        educational
                                        records, and contact information — for the purpose of verifying my profile and
                                        operating
                                        the Succour Trust Registry. I authorise Succour to contact my references,
                                        guarantor,
                                        previous employers, and educational institutions to verify the information
                                        provided.
                                        I
                                        understand that my data may be shared with prospective employers solely in the
                                        context
                                        of care placement and in accordance with applicable data protection laws (NDPR).
                                        I
                                        confirm that all information provided is accurate and complete to the best of my
                                        knowledge.
                                    </div>
                                </div>
                            </div>
                            <div id="consent-error" style="font-size:11px;color:var(--red);display:none;">⚠ You must
                                accept
                                the
                                consent declaration to proceed.</div>
                        </div>

                        <button class="submit-btn" @click="goToStep(2)">Continue — Experience →</button>
                    </div>

                    <!-- ── STEP 2: EXPERIENCE ── -->
                    <div id="reg-step-2" v-show="currentStep === 2">
                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">💼</div> Care Categories <span class="section-hint">* Select
                                    at least
                                    one</span>
                            </div>
                            <div class="cat-grid" id="cat-grid">
                                <div class="cat-toggle" v-for="cat in categories" :key="cat.id"
                                    :class="{ selected: selectedCats.includes(cat.id) }" @click="toggleCat(cat.id)">
                                    {{ cat.icon }} {{ cat.name }}
                                </div>
                            </div>
                            <div v-if="errors.categories" class="field-error">
                                {{ errors.categories }}
                            </div>
                        </div>

                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">⭐</div> Work Experience
                            </div>
                            <div class="form-grid">
                                <div class="field">
                                    <label>Type of Experience <span class="req">*</span></label>
                                    <select id="r-exp-type" v-model="form.expType" @change="clearError('expType')">
                                        <option value="">— Select —</option>
                                        <option value="formal">Formal (employed by family/agency)</option>
                                        <option value="informal">Informal (family/relatives)</option>
                                        <option value="both">Both formal and informal</option>
                                        <option value="none">No prior experience</option>
                                    </select>
                                    <div v-if="errors.expType" class="field-error">
                                        {{ errors.expType }}
                                    </div>
                                </div>
                                <div class="field">
                                    <label>Years of Experience <span class="req">*</span></label>
                                    <select id="r-exp-years" v-model="form.expYears" @change="clearError('expYears')">
                                        <option value="">— Select —</option>
                                        <option value="0">Less than 1 year</option>
                                        <option value="1">1 – 2 years</option>
                                        <option value="3">3 – 5 years</option>
                                        <option value="6">6 – 10 years</option>
                                        <option value="10">Over 10 years</option>
                                    </select>
                                    <div v-if="errors.expYears" class="field-error">
                                        {{ errors.expYears }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-grid">
                                <div class="field">
                                    <label>Preferred Arrangement <span class="req">*</span></label>
                                    <select id="r-arrangement" v-model="form.arrangement"
                                        @change="clearError('arrangement')">
                                        <option value="">— Select —</option>
                                        <option value="live_in">Live-in</option>
                                        <option value="live_out">Live-out</option>
                                        <option value="both">Both</option>
                                    </select>
                                    <div v-if="errors.arrangement" class="field-error">
                                        {{ errors.arrangement }}
                                    </div>
                                </div>
                                <div class="field">
                                    <label>Availability <span class="req">*</span></label>
                                    <select id="r-availability" v-model="form.availability"
                                        @change="clearError('availability')">
                                        <option value="">— Select —</option>
                                        <option value="immediately">Available immediately</option>
                                        <option value="1_week">Available within 1 week</option>
                                        <option value="2_4_weeks">Available within 2–4 weeks</option>
                                        <option value="1_month_plus">Available in 1+ month</option>
                                    </select>
                                    <div v-if="errors.availability" class="field-error">
                                        {{ errors.availability }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-grid-1">
                                <div class="field">
                                    <label>Brief About Yourself <span class="req">*</span></label>
                                    <textarea id="r-bio" @input="clearError('bio')" v-model="form.bio"
                                        placeholder="Tell families a little about yourself, your experience and why you love care work..."></textarea>

                                    <div class="word-count">
                                        {{ 100 - wordCount }} words left
                                    </div>
                                </div>
                                <div v-if="errors.bio" class="field-error">
                                    {{ errors.bio }}
                                </div>
                            </div>
                        </div>

                        <!-- Previous Employers -->
                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">📞</div> Previous Employers <span
                                    class="section-hint">(Optional)</span>
                            </div>
                            <div class="notice info">Provide name and phone number for each reference.
                            </div>

                            <div class="section-card" v-for="(ref, index) in employments" :key="index">
                                <button type="button" class="ref-remove-btn" @click="removeEmployer(index)">
                                    ×
                                </button>
                                <div class="ref-row-num">
                                    Employer Reference {{ index + 1 }}
                                </div>
                                <div class="divider"></div>
                                <div class="form-grid">

                                    <div class="field">
                                        <div class="ref-row-num">
                                            Full Name
                                        </div>


                                        <input type="text" placeholder="Full name" v-model="ref.name"
                                            @input="clearRefError(index, 'name')" />

                                        <div v-if="errors[`ref_name_${index}`]" class="field-error">
                                            {{ errors[`ref_name_${index}`] }}
                                        </div>


                                    </div>
                                    <div class="field">

                                        <div class="ref-row-num">
                                            Phone Number
                                        </div>

                                        <input type="tel" placeholder="0800 000 0000" v-model="ref.phone"
                                            @input="onRefPhoneInput($event, index)" />
                                        <div v-if="errors[`ref_phone_${index}`]" class="field-error">
                                            {{ errors[`ref_phone_${index}`] }}
                                        </div>

                                    </div>

                                </div>


                                <div class="form-grid">
                                    <div class="field">

                                        <div class="ref-row-num">
                                            Email
                                        </div>

                                        <input type="email" placeholder="amaka@email.com" v-model="ref.email"
                                            @input="clearRefError(index, 'email')" />
                                        <div v-if="errors[`ref_email_${index}`]" class="field-error">
                                            {{ errors[`ref_email_${index}`] }}
                                        </div>

                                    </div>

                                    <div class="field">

                                        <div class="ref-row-num">
                                            Address
                                        </div>

                                        <input type="text" placeholder="Residential Address" v-model="ref.address"
                                            @input="clearRefError(index, 'address')" />
                                        <div v-if="errors[`ref_address_${index}`]" class="field-error">
                                            {{ errors[`ref_address_${index}`] }}
                                        </div>

                                    </div>
                                </div>

                            </div>


                            <div v-if="errors.employments" class="field-error">
                                {{ errors.employments }}
                            </div>
                            <button class="add-ref-btn" id="add-ref-btn" @click="addEmployer()">+ Add
                                Previous Employer</button>
                        </div>

                        <div class="section-card">
                            <div class="section-title">
                                <div class="section-icon">🎓</div> Education & Qualifications
                            </div>
                            <div class="form-grid">
                                <div class="field">
                                    <label>Highest Education Level <span class="req">*</span></label>
                                    <select id="r-edu" v-model="form.edu" @change="clearError('edu')">
                                        <option value="">— Select —</option>

                                        <option v-for="edu in educationLevels" :key="edu.id" :value="edu.id">
                                            {{ edu.name }}
                                        </option>

                                    </select>
                                    <div v-if="errors.edu" class="field-error">
                                        {{ errors.edu }}
                                    </div>
                                </div>
                                <div class="field">
                                    <label>English / Communication Level <span class="req">*</span></label>
                                    <select id="r-english" v-model="form.english" @change="clearError('english')">
                                        <option value="">— Select —</option>

                                        <option v-for="level in englishLevels" :key="level.id" :value="level.id">
                                            {{ level.name }}
                                        </option>
                                    </select>
                                    <div v-if="errors.english" class="field-error">
                                        {{ errors.english }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style="display:flex;gap:12px;">
                            <button class="submit-btn"
                                style="background:white;color:var(--dkgreen);border:1px solid var(--border);flex:0 0 120px;"
                                @click="goToStep(1)">← Back</button>
                            <button class="submit-btn" @click="goToStep(3)">Continue — Documents →</button>
                        </div>
                    </div>

                    <!-- ── STEP 3: DOCUMENTS ── -->
                    <div v-show="currentStep === 3">

                        <div class="notice info">
                            📋 Upload clear, readable photos or scans. All documents are securely stored
                            for verification purposes only.
                        </div>

                        <!-- ID NUMBERS -->
                        <div class="section-card">

                            <div class="section-title">
                                <div class="section-icon">🔢</div>
                                Identity Numbers
                                <span class="section-hint">
                                    * Both required
                                </span>
                            </div>

                            <div class="form-grid">

                                <div class="field">
                                    <label>BVN Number <span class="req">*</span></label>

                                    <input type="text" v-model="form.bvn" maxlength="11"
                                        placeholder="Enter your 11-digit BVN" @input="clearError('bvn')"
                                        :class="{ invalid: errors.bvn }" />

                                    <div v-if="bvnStatus.loading" class="field-hint">
                                        Verifying BVN...
                                    </div>

                                    <div v-if="bvnStatus.status === 'api_verified'" class="field-hint"
                                        style="color:green;">
                                        ✅ {{ bvnStatus.status === 'api_verified' ? "API Verified" : "API Pending" }}
                                    </div>

                                    <div v-else class="field-hint" style="color:red;">
                                        ❌ {{ bvnStatus.status === 'api_failed' ? "API Failed / Not Found" : "API Pending" }}
                                    </div>


                                    <div v-if="errors.bvn" class="field-error">
                                        ❌ {{ errors.bvn }}
                                    </div>

                                    <div v-if="!bvnStatus.loading && bvnStatus.message && bvnStatus.status === 'api_pending'"
                                        class="field-error">
                                        {{ bvnStatus.message }}
                                    </div>

                                    <div class="field-hint">
                                        Verified via Monnify API — automated
                                    </div>
                                </div>

                                <div class="field">
                                    <label>NIN Number <span class="req">*</span></label>

                                    <input type="text" v-model="form.nin" maxlength="11"
                                        placeholder="Enter your 11-digit NIN" @input="clearError('nin')"
                                        :class="{ invalid: errors.nin }" />


                                    <div v-if="ninStatus.loading" class="field-hint">
                                        Verifying NIN...
                                    </div>

                                    <div v-if="ninStatus.status === 'api_verified'" class="field-hint"
                                        style="color:green;">
                                        ✅ {{ ninStatus.status === "api_verified" ? "API Verified" : "API Pending" }}
                                    </div>

                                    <div v-else class="field-hint" style="color:red;">
                                        ❌ {{ ninStatus === "api_failed" ? "API Failed / Not Found" : "API Pending" }}
                                    </div>


                                    <div v-if="errors.nin" class="field-error">
                                        ❌ {{ errors.nin }}
                                    </div>

                                    <div v-if="!ninStatus.loading && ninStatus.message && ninStatus.status === 'api_pending'"
                                        class="field-error">
                                        {{ ninStatus.message }}
                                    </div>

                                    <div class="field-hint">
                                        Verified via NIMC — automated
                                    </div>
                                </div>

                            </div>
                        </div>

                        <!-- GOVERNMENT ID -->
                        <div class="section-card">

                            <div class="section-title">
                                <div class="section-icon">🪪</div>
                                Government-Issued ID
                                <span class="section-hint">
                                    * Required
                                </span>
                            </div>

                            <div class="notice warn">
                                Upload a clear image of your NIN slip, Voter's Card, Driver's Licence or International
                                Passport.
                            </div>

                            <div class="dual-upload">

                                <label class="dual-btn">
                                    <span>📷</span>
                                    Take a clear photo

                                    <input type="file" ref="idCameraInput" accept="image/*" capture="environment"
                                        @change="handleDocUpload('id', $event)" hidden />
                                </label>

                                <label class="dual-btn">
                                    <span>📁</span>
                                    Upload from device

                                    <input type="file" ref="idFileInput" accept="image/*,.pdf"
                                        @change="handleDocUpload('id', $event)" hidden />
                                </label>

                            </div>

                            <div v-if="docs.id.file || docs.id.preview" class="doc-uploaded-card">

                                <!-- Preview -->
                                <div class="doc-thumb">

                                    <!-- Image Preview -->
                                    <template v-if="docs.id.preview && docs.id.type === 'image'">
                                        <img :src="docs.id.preview" alt="Document Preview" />
                                    </template>

                                    <!-- PDF Preview -->
                                    <template v-else>
                                        <div class="pdf-thumb">
                                            📄
                                        </div>
                                    </template>

                                </div>

                                <!-- File Info -->
                                <div class="doc-meta">
                                    <div class="doc-label">
                                        Uploaded Document
                                    </div>

                                    <div class="doc-name">
                                        {{ docs.id.file?.name || getFileName(docs.id.preview) }}
                                    </div>
                                </div>

                                <!-- Remove -->
                                <button type="button" class="doc-remove-btn" @click="removeDoc('id')">
                                    Remove
                                </button>

                            </div>
                            <div v-if="docs.id.file" style="margin-top:10px;color:green;font-size:13px;">
                                ✅ File selected successfully
                            </div>

                            <div v-if="errors.id" class="field-error">
                                {{ errors.id }}
                            </div>

                        </div>

                        <!-- EDUCATION -->
                        <div class="section-card">

                            <div class="section-title">
                                <div class="section-icon">🎓</div>
                                Education Certificate
                            </div>
                            <div class="field">
                                <label>Upload Highest Certificate</label>

                                <div class="doc-upload-zone" @click="$refs.certInput.click()">

                                    <div class="doc-icon">📄</div>

                                    <div class="doc-upload-label">
                                        Click to upload certificate
                                    </div>

                                    <div class="doc-upload-sub">
                                        WAEC, OND, HND, B.Sc or equivalent · JPG, PNG or PDF
                                    </div>

                                    <input ref="certInput" type="file" accept="image/*,.pdf"
                                        @change="handleDocUpload('cert', $event)" hidden />

                                </div>

                                <div v-if="docs.cert.file || docs.cert.preview" class="doc-uploaded-card">

                                    <!-- Preview -->
                                    <div class="doc-thumb">

                                        <!-- Image Preview -->
                                        <template v-if="docs.cert.preview && docs.cert.type === 'image'">
                                            <img :src="docs.cert.preview" alt="Document Preview" />
                                        </template>

                                        <!-- PDF Preview -->
                                        <template v-else>
                                            <div class="pdf-thumb">
                                                📄
                                            </div>
                                        </template>

                                    </div>

                                    <!-- File Info -->
                                    <div class="doc-meta">
                                        <div class="doc-label">
                                            Uploaded Document
                                        </div>

                                        <div class="doc-name">
                                            {{ docs.cert.file?.name || getFileName(docs.cert.preview) }}
                                        </div>
                                    </div>

                                    <!-- Remove -->
                                    <button type="button" class="doc-remove-btn" @click="removeDoc('cert')">
                                        Remove
                                    </button>

                                </div>

                                <div v-if="docs.cert.file" style="margin-top:10px;color:green;font-size:13px;">
                                    ✅ File selected successfully
                                </div>

                                <div v-if="errors.cert" class="field-error">
                                    {{ errors.cert }}
                                </div>

                            </div>

                        </div>

                        <!-- GUARANTOR -->
                        <div class="section-card">

                            <div class="section-title">
                                <div class="section-icon">👥</div>
                                Guarantor Information
                                <span class="section-hint">
                                    * Required
                                </span>
                            </div>

                            <div class="notice warn">
                                ⚠ A guarantor is required for deployment. Must be a responsible adult who can vouch for
                                your
                                character — not
                                a family member.
                            </div>

                            <div class="form-grid">

                                <div class="field">
                                    <label>Guarantor Full Name <span class="req">*</span></label>

                                    <input type="text" v-model="form.gname" @input="clearError('gname')"
                                        placeholder="Full name" />

                                    <div v-if="errors.gname" class="field-error">
                                        {{ errors.gname }}
                                    </div>
                                </div>

                                <div class="field">
                                    <label>Relationship to you <span class="req">*</span></label>

                                    <input type="text" v-model="form.grel" @input="clearError('grel')"
                                        placeholder="e.g. Employer, Pastor, Teacher" />

                                    <div v-if="errors.grel" class="field-error">
                                        {{ errors.grel }}
                                    </div>
                                </div>

                            </div>

                            <div class="form-grid">

                                <div class="field">
                                    <label>Guarantor Phone Number <span class="req">*</span></label>

                                    <input type="tel" v-model="form.gphone" @input="onGphoneInput"
                                        placeholder="0800 000 0000" />

                                    <div v-if="errors.gphone" class="field-error">
                                        {{ errors.gphone }}
                                    </div>
                                </div>

                                <div class="field">
                                    <label>Guarantor Occupation <span class="req">*</span></label>

                                    <input type="text" v-model="form.gocc" @input="clearError('gocc')"
                                        placeholder="e.g. Civil Servant" />

                                    <div v-if="errors.gocc" class="field-error">
                                        {{ errors.gocc }}
                                    </div>
                                </div>

                            </div>
                            <div class="form-grid-1">
                                <div class="field">
                                    <label for="">Residential Address<span class="req">*</span></label>
                                    <input type="text" v-model="form.g_address"
                                        placeholder="House number, street, city, state"
                                        @input="clearError('g_address')" />
                                    <div v-if="errors.g_address" class="field-error">
                                        {{ errors.g_address }}
                                    </div>
                                    <div class="format-hint">For records only — used to locate your guarantor if needed
                                        during verification
                                    </div>


                                </div>

                            </div>

                            <hr class="divider">

                            <div class="section-label">Identity Verification</div>

                            <div class="form-grid-1">
                                <div class="field">
                                    <label style="margin-bottom:10px;">
                                        ID Type<span class="req">*</span>
                                    </label>

                                    <div class="id-types">
                                        <div class="id-type-btn" :class="{ active: form.g_id_type === 'license' }"
                                            @click="selectID('license', 'Driver\'s License No.')">
                                            <div class="cb"></div>Driver's License
                                        </div>

                                        <div class="id-type-btn" :class="{ active: form.g_id_type === 'passport' }"
                                            @click="selectID('passport', 'Passport No. e.g. A12345678')">
                                            <div class="cb"></div>Passport
                                        </div>

                                        <div class="id-type-btn" :class="{ active: form.g_id_type === 'voters_card' }"
                                            @click="selectID('voters_card', ' Voter ID No.')">
                                            <div class="cb"></div>Voter's Card
                                        </div>

                                        <div class="id-type-btn" :class="{ active: form.g_id_type === 'nin' }"
                                            @click="selectID('nin', '11-digit NIN')">
                                            <div class="cb"></div>NIN
                                        </div>
                                    </div>
                                </div>
                                <div v-if="errors.id_type" class="field-error">
                                    {{ errors.id_type }}
                                </div>
                            </div>

                            <div class="form-grid-1">
                                <div class="field">
                                    <label>ID Number<span class="req">*</span></label>

                                    <input id="id-number" v-model="form.g_id_num" :disabled="!form.g_id_type"
                                        @input="clearError('g_id_num')" type="text" :placeholder="idPlaceholder" />
                                </div>
                                <div v-if="errors.g_id_num" class="field-error">
                                    {{ errors.g_id_num }}
                                </div>
                            </div>


                            <hr class="divider">

                            <div class="field" style="margin-bottom:14px;">
                                <div class="section-label">Guarantor Letter</div>

                                <label style="margin-bottom:10px;">Upload Letter <span
                                        class="opt">(Optional)</span></label>
                                <div class="doc-upload-zone" @click="$refs.gInput.click()">
                                    <div class="doc-icon">📝</div>
                                    <div class="doc-upload-label">Click to upload signed guarantor letter</div>
                                    <div class="doc-upload-sub">Signed letter from guarantor · JPG, PNG or PDF</div>
                                    <input type="file" ref="gInput" accept="image/*,.pdf"
                                        @change="handleDocUpload('guarantor', $event)" hidden>
                                </div>
                                <div v-if="docs.guarantor.file || docs.guarantor.preview" class="doc-uploaded-card">

                                    <!-- Preview -->
                                    <div class="doc-thumb">

                                        <!-- Image Preview -->
                                        <template v-if="docs.guarantor.preview && docs.guarantor.type === 'image'">
                                            <img :src="docs.guarantor.preview" alt="Document Preview" />
                                        </template>

                                        <!-- PDF Preview -->
                                        <template v-else>
                                            <div class="pdf-thumb">
                                                📄
                                            </div>
                                        </template>

                                    </div>

                                    <!-- File Info -->
                                    <div class="doc-meta">
                                        <div class="doc-label">
                                            Uploaded Document
                                        </div>

                                        <div class="doc-name">
                                            {{ docs.guarantor.file?.name || getFileName(docs.guarantor.preview) }}
                                        </div>
                                    </div>

                                    <!-- Remove -->
                                    <button type="button" class="doc-remove-btn" @click="removeDoc('guarantor')">
                                        Remove
                                    </button>

                                </div>

                                <div v-if="docs.guarantor.file" style="margin-top:10px;color:green;font-size:13px;">
                                    ✅ File selected successfully
                                </div>

                                <div v-if="errors.guarantor" class="field-error">
                                    {{ errors.guarantor }}
                                </div>
                            </div>

                            <div class="upload-hint">
                                <i class="ti ti-info-circle" aria-hidden="true"></i>
                                <span>Uploading a letter from your guarantor improves your Trust Score and increases
                                    your chances of being seen by a Care Seeker.</span>
                            </div>



                            <button class="dl-btn" @click="downloadPDF()">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                <i class="ti ti-download" aria-hidden="true"></i>
                                Download Guarantor Letter Template (PDF)
                            </button>

                        </div>

                        <!-- EMPLOYMENT -->
                        <div class="section-card">

                            <div class="section-title">
                                <div class="section-icon">💼</div>
                                Employment Letters (optional)
                            </div>

                            <div class="doc-upload-zone" @click="$refs.empInput.click()">

                                <div class="doc-icon">📋</div>

                                <div class="doc-upload-label">
                                    Click to upload employment letter
                                </div>

                                <div class="doc-upload-sub">
                                    Offer letter or reference from previous employer
                                </div>

                                <input ref="empInput" type="file" accept="image/*,.pdf"
                                    @change="handleDocUpload('emp', $event)" hidden />

                            </div>

                            <div v-if="docs.emp.file || docs.emp.preview" class="doc-uploaded-card">

                                <!-- Preview -->
                                <div class="doc-thumb">

                                    <!-- Image Preview -->
                                    <template v-if="docs.emp.preview && docs.emp.type === 'image'">
                                        <img :src="docs.emp.preview" alt="Document Preview" />
                                    </template>

                                    <!-- PDF Preview -->
                                    <template v-else>
                                        <div class="pdf-thumb">
                                            📄
                                        </div>
                                    </template>

                                </div>

                                <!-- File Info -->
                                <div class="doc-meta">
                                    <div class="doc-label">
                                        Uploaded Document
                                    </div>

                                    <div class="doc-name">
                                        {{ docs.emp.file?.name || getFileName(docs.emp.preview) }}
                                    </div>
                                </div>

                                <!-- Remove -->
                                <button type="button" class="doc-remove-btn" @click="removeDoc('emp')">
                                    Remove
                                </button>

                            </div>

                            <div v-if="docs.emp.file" style="margin-top:10px;color:green;font-size:13px;">
                                ✅ File selected successfully
                            </div>

                        </div>

                        <!-- NAV -->
                        <div style="display:flex;gap:12px;">

                            <button class="submit-btn" @click="goToStep(2)"
                                style="background:white;color:var(--dkgreen);border:1px solid var(--border);flex:0 0 120px;">
                                ← Back
                            </button>

                            <button class="submit-btn" @click="goToStep(4)">
                                Review & Submit →
                            </button>

                        </div>

                    </div>

                    <!-- ── STEP 4: REVIEW ── -->
                    <div id="reg-step-4" v-show="currentStep === 4">
                        <div class="section-card" id="review-summary">
                            <div class="section-title">
                                <div class="section-icon">✅</div> Application Summary
                            </div>
                            <div v-html="reviewHTML" style="font-size:13px;color:var(--text);line-height:2;"></div>
                        </div>
                        <div class="notice info">By submitting this form you confirm all information is accurate and
                            that
                            you
                            have accepted the consent declaration above. Succour will contact you within 24 hours to
                            begin
                            verification.</div>
                        <div style="display:flex;gap:12px;">
                            <button class="submit-btn"
                                style="background:white;color:var(--dkgreen);border:1px solid var(--border);flex:0 0 120px;"
                                @click="goToStep(3)">← Back</button>
                            <button class="submit-btn" @click="submitRegistration()">Submit Application 💚</button>
                        </div>
                    </div>

                    <!-- SUCCESS -->
                    <div class="success-state" v-show="currentStep === 5">
                        <div class="success-icon">💚</div>
                        <div class="success-title">Application Received!</div>
                        <div class="success-sub">Thank you for registering with Succour. Our team will contact you
                            within 24
                            hours to begin your verification process.</div>
                        <div class="success-ref" id="success-ref">REF: {{ successRef }}</div>
                    </div>
                </div>

            </div>


            <div class="overlay show" v-if="showOtpModal && currentStep === 1 && !emailVerified">
                <div class="modal">

                    <div class="modal-handle">
                        <div class="modal-handle-bar"></div>
                    </div>

                    <div class="modal-topbar">

                        <button class="modal-close-btn" @click="dismissModal" v-if="!successState">
                            <svg viewBox="0 0 24 24">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>

                    </div>

                    <div class="modal-body">

                        <!-- OTP STATE -->
                        <div>

                            <div class="modal-icon">
                                <svg viewBox="0 0 24 24">
                                    <path
                                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z">
                                    </path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                            </div>

                            <div class="modal-title">
                                Check your email
                            </div>

                            <div class="modal-sub">
                                An OTP code has been sent to
                                <br>

                                <strong>
                                    {{ form.email }}
                                </strong>

                                <br>

                                Enter the 6-digit code to continue.
                            </div>

                            <div class="otp-row">

                                <input v-for="(digit, index) in otp" :key="index" :ref="'otpInputs'"
                                    v-model="otp[index]" :class="['otp-box', { filled: otp[index] }]" maxlength="1"
                                    inputmode="numeric" pattern="[0-9]*" @input="onOtpInput(index, $event)"
                                    @keydown="onOtpKeydown(index, $event)" @paste="onOtpPaste" />

                            </div>

                            <div v-if="otpError" class="otp-error">
                                {{ otpError }}
                            </div>

                            <button class="verify-btn" :disabled="verifyBtnDisabled" @click="verifyOtp">
                                Verify Email
                            </button>

                            <div class="resend-row">

                                Didn't get the code?

                                <button class="resend-btn" :disabled="resendDisabled" @click="resendOtp">
                                    Resend
                                </button>

                                <span v-if="resendDisabled">
                                    in {{ formattedTimer() }}s
                                </span>

                            </div>

                        </div>

                       

                    </div>

                </div>

            </div>
        </body>



    </HomeLayout>

</template>

<script lang="js">
import HomeLayout from '../Layouts/HomeLayout.vue';
import Request from "@/utils/apiclient.js";
import { formatErrorMessages } from "@/utils/errorHandler";
import ToastManagerVue from "@/components/ToastAlert/ToastManager.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import statesData from "@/data/states.js";


export default {
    name: "OnboardingCaregiver",
    components: {
        HomeLayout,
        ToastManagerVue,
        LoadingSpinner,

    },
    data() {
        return {
            states: statesData.states,
            selectedCats: [],
            caregiverId: null,
            loading: false,
            photoDataURL: null,
            errors: {},
            sameAsPhone: false,
            idPlaceholder: "Select an ID type above",

            categories: [],
            educationLevels: [],
            englishLevels: [],


            emailVerified: false,
            verifiedEmail: null,
            emailExist: false,
            verificationLoading: false,
            checkLoading: false,

            showOtpModal: false,
            otp: ["", "", "", "", "", ""],

            otpError: "",
            verifyBtnDisabled: true,

            resendDisabled: true,
            timer: 90,
            timerInterval: null,

            successState: false,

            // STEP NAV
            currentStep: 1,

            // FORM MODEL (bind these with v-model in template)
            form: {
                firstname: '',
                lastname: '',
                dob: '',
                gender: '',
                phone: '',
                whatsapp: '',
                email: '',
                state: '',
                relocate: '',
                consent: false,

                expType: '',
                expYears: '',
                arrangement: '',
                availability: '',
                bio: '',
                edu: '',
                english: '',

                bvn: '',
                nin: '',

                gname: '',
                gphone: '',
                grel: '',
                gocc: '',
                g_address: '',
                g_id_type: '',
                g_id_num: ''
            },

            employments: [],
            docs: {
                id: {
                    file: null,
                    preview: null,
                    type: null
                },
                cert: {
                    file: null,
                    preview: null,
                    type: null
                },
                guarantor: {
                    file: null,
                    preview: null,
                    type: null
                },
                emp: {
                    file: null,
                    preview: null,
                    type: null
                }
            },

            bvnStatus: {
                loading: false,
                status: "api_pending",
                message: '',
                data: null
            },

            ninStatus: {
                loading: false,
                status: "api_pending",
                message: '',
                data: null
            },

            reviewHTML: '',
            successRef: '',
            PDF_B64: "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",

            isLoading: false,
        };
    },
    computed: {

        maxDOB() {

            const today = new Date();

            today.setFullYear(today.getFullYear() - 18);

            return today.toISOString().split('T')[0];
        },

        wordCount() {
            return (this.form.bio || '')
                .trim()
                .split(/\s+/)
                .filter(word => word.length > 0).length;
        },
        selectedCategoryNames() {
            return this.selectedCats
                .map(id => {
                    const cat = this.categories.find(c => c.id === id);
                    return cat ? cat.name : null;
                })
                .filter(Boolean)
                .join(', ');
        }
    },

    watch: {
         "form.email"(value) {

        const currentEmail = value?.trim().toLowerCase();
        const verifiedEmail = this.verifiedEmail?.trim().toLowerCase();

        if (
            this.emailVerified &&
            verifiedEmail &&
            currentEmail !== verifiedEmail
        ) {
            this.emailVerified = false;
            this.verifiedEmail = null;
        }

        if (this.emailVerified) return;

        clearTimeout(this.emailDebounce);

        this.emailDebounce = setTimeout(() => {
            if (this.isValidEmail(currentEmail)) {
                this.checkEmail();
            }
        }, 700);
    },

        employments: {
            deep: true,
            handler(val) {

                if (val.length > 0) {
                    delete this.errors.employments;
                }
            }
        },

        'form.phone'(newVal) {

            if (this.sameAsPhone) {
                this.form.whatsapp = newVal;
            }
        },
        'form.bvn'(value) {

            // only digits
            this.form.bvn = value.replace(/\D/g, '');

            if (this.form.bvn.length === 11) {
                this.verifyBVN();
            } else {
                this.bvnStatus = {
                    loading: false,
                    status: "api_pending",
                    message: '',
                    data: null
                };
            }
        },

        'form.nin'(value) {

            // only digits
            this.form.nin = value.replace(/\D/g, '');

            if (this.form.nin.length === 11) {
                this.verifyNIN();
            } else {
                this.ninStatus = {
                    loading: false,
                    status: "api_pending",
                    message: '',
                    data: null
                };
            }
        },


    },
    beforeUnmount() {
        clearInterval(this.timerInterval);
    },

    mounted() {

        this.preloadedData();


    },



    methods: {
        getFileName(url) {
            if (!url) return 'Uploaded document';
            return url.split('/').pop();
        },
        restoreDocuments(data) {

            if (data.id) {
                this.docs.id = {
                    file: null,
                    preview: data.id.url,
                    type: data.id.url.endsWith('.pdf') ? 'pdf' : 'image'
                };
            }

            if (data.certificate) {
                this.docs.cert = {
                    file: null,
                    preview: data.certificate.url,
                    type: data.certificate.url.endsWith('.pdf') ? 'pdf' : 'image'
                };
            }

            if (data.guarantor) {
                this.docs.guarantor = {
                    file: null,
                    preview: data.guarantor.url,
                    type: data.guarantor.url.endsWith('.pdf') ? 'pdf' : 'image'
                };
            }

            if (data.employment) {
                this.docs.emp = {
                    file: null,
                    preview: data.employment.url,
                    type: data.employment.url.endsWith('.pdf') ? 'pdf' : 'image'
                };
            }
        },
        async preloadedData() {
            this.isLoading = true;

            await this.loadMeta();

            await this.restoreOnboarding();

            // only create empty row if none restored
            // if (!this.employments.length) {
            //     this.addEmployer();
            // }

            this.isLoading = false;
        },

        async loadMeta() {
            try {

                this.loading = true;

                const request = new Request(
                    '/registration-meta/'
                );

                await request.get((res, error) => {

                    if (error) {
                        console.log("FETCH ERROR", error);
                        return;
                    }

                    console.log("META DATA", res);

                    this.categories = res.categories

                    this.educationLevels =
                        res.education_levels

                    this.englishLevels =
                        res.english_levels


                }, false);

            } catch (err) {

                console.log("FETCH FAILED", err);

            } finally {

                this.loading = false;
            }
        },

        async restoreOnboarding() {

            this.isLoading = true;

            const savedId =
                sessionStorage.getItem(
                    "caregiver_id"
                );

            if (savedId) {

                this.caregiverId = savedId;

                await this.fetchOnboarding();
            }

            this.isLoading = false;
        },

        sanitizePhone(value) {

            // remove non-digits
            value = value.replace(/\D/g, '');

            // max 11 digits
            value = value.slice(0, 11);

            // enforce Nigerian format:
            // 0[1-9]0XXXXXXXX
            if (value.length >= 1 && value[0] !== '0') {
                value = '0';
            }

            // second digit must be 1-9
            if (value.length >= 2) {
                const second = value[1];

                if (!/[1-9]/.test(second)) {
                    value = value[0];
                }
            }

            // third digit must be 0
            // if (value.length >= 3 && value[2] !== '0') {
            //     value = value.slice(0, 2);
            // }

            return value;
        },

        onPhoneInput(e) {
            this.form.phone = this.sanitizePhone(e.target.value);
            this.clearError('phone');
        },

        onWhatsappInput(e) {
            this.form.whatsapp = this.sanitizePhone(e.target.value);
            this.clearError('whatsapp');
        },

        onRefPhoneInput(e, index) {
            if (!this.employments[index]) {
                this.employments[index] = {
                    name: '',
                    phone: '',
                    email: '',
                    address: '',
                };
            }

            this.employments[index].phone = this.sanitizePhone(e.target.value);

            this.clearRefError(index, 'phone');
        },

        onGphoneInput(e) {
            this.form.gphone = this.sanitizePhone(e.target.value);
            this.clearError('gphone');
        },
        isValidEmail(email) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
        },
        normalizePhone(phone) {

            // remove spaces and non-digits
            let cleaned = phone.replace(/\D/g, '');

            // enforce exactly 11 digits
            if (cleaned.length !== 11) {
                return null;
            }

            // enforce Nigerian mobile format:
            // 0[1-9]0XXXXXXXX
            if (!/^0[1-9][0-9]\d{8}$/.test(cleaned)) {
                return null;
            }

            // return local Nigerian format
            return cleaned;
        },
        showToast(msg, statusValue) {
            this.toastManager?.addToast(msg, statusValue);
        },
        clearError(field) {
            delete this.errors[field];
        },
        clearRefError(index, field) {

            const key = `ref_${field}_${index}`;

            delete this.errors[key];
        },
        toggleSameWhatsapp() {

            if (this.sameAsPhone) {

                this.form.whatsapp = this.form.phone;

                delete this.errors.whatsapp;

            } else {

                this.form.whatsapp = '';
            }
        },

        selectID(type, placeholder) {
            this.form.g_id_type = type;
            this.idPlaceholder = placeholder;

            // optional UX improvement: focus input
            this.$nextTick(() => {
                const input = document.getElementById("id-number");
                if (input) input.focus();
            });
            delete this.errors.id_type;

        },
        async checkEmail() {

            if (this.checkLoading) return;

            if (!this.form.email?.trim()) {
                return;
            }

            this.errors.email = null;

            try {

                this.checkLoading = true;

                const request = new Request(`/auth/check-exist/?email=${this.form.email}`);

                await request.get(
                    (res, error) => {

                        if (error) {

                            console.log("EMAIL EXIST ERROR", error);

                            if (error.status === "failure") {
                                this.errors.email = error.message;
                                return;
                            }

                            if (error.detail) {
                                this.errors.email = error.detail;
                                return;
                            }

                            this.errors.email = "Unable to verify email.";
                            return;
                        }

                        console.log("EMAIL CHECK SUCCESS", res);

                        if (res.exists) {
                            this.errors.email =
                                res.message || "Email already exists.";
                                this.emailExist = res.exists;
                            return;
                        }

                        this.errors.email = null;

                        this.sendVerificationEmail();

                    },
                    false
                );

            } catch (err) {

                console.log("UNEXPECTED ERROR", err);
                this.errors.email = "Unable to check email.";

            } finally {

                this.checkLoading = false;
            }
        },
        async sendVerificationEmail() {

            if (this.verificationLoading) return;
            console.log(this.form.email)

            try {

                this.verificationLoading = true;

                const request = new Request(
                    "/auth/send-email-verification/",
                );

                await request.post(
                    {
                        email: this.form.email,
                        name: this.form.firstname,
                    },
                    (res, error) => {

                        if (error) {

                            console.log("EMAIL VERIFICATION ERROR", error);

                            if (error.status === "failure") {
                                this.errors.email = error.message;
                                this.showToast(error.message, "error");
                                return;
                            }

                            if (error.detail) {
                                this.errors.email = error.detail;
                                this.showToast(error.detail, "error");
                                return;
                            }

                            const errorMessages = formatErrorMessages(error);

                            errorMessages.forEach((message) => {
                                this.showToast(message, "error");
                            });

                            return;
                        }

                        console.log("EMAIL VERIFICATION SUCCESS", res);

                        this.showToast(
                            "Verification email sent successfully",
                            "success"
                        );

                        this.openModal();
                    }, false
                );

            } catch (err) {

                console.log("UNEXPECTED ERROR", err);

                this.errors.email = "Unable to send verification email.";

                this.showToast(
                    "Unable to send verification email.",
                    "error"
                );

            } finally {

                this.verificationLoading = false;
            }
        },

        openModal() {
            console.log("OPEN MODAL CALLED");

            if (this.showOtpModal) return;

            this.resetOtp();

            this.showOtpModal = true;
            this.successState = false;

            document.body.style.overflow = "hidden";

            this.startTimer();

            this.$nextTick(() => {
                this.$refs.otpInputs?.[0]?.focus();
            });
        },

        dismissModal() {

            this.showOtpModal = false;

            document.body.style.overflow = "";

            clearInterval(this.timerInterval);
        },

        closeModal() {

            this.showOtpModal = false;

            document.body.style.overflow = "";
        },

        async verifyOtp() {

            try {

                this.otpError = "";

                const code = this.otp.join("");

                const request = new Request(
                    "/auth/verify-email-otp/"
                );

                await request.post(
                    {
                        email: this.form.email,
                        otp: code,
                    },
                    (res, error) => {

                        if (error) {

                            console.log("OTP VERIFICATION ERROR", error);

                            if (error.error) {
                                console.log("it got here")
                                this.otpError = error.error;
                                console.log(error.error)
                                console.log(this.otpError)

                                // this.showToast(error.error, "error");

                            } else if (error.status === "failure") {

                                this.otpError = error.status;

                                this.showToast(error.message, "error");

                            } else if (error.detail) {

                                this.otpError = error.detail;

                                this.showToast(error.detail, "error");

                            } else {

                                const errorMessages =
                                    formatErrorMessages(error);

                                this.otpError =
                                    errorMessages[0] ||
                                    "Incorrect or expired code.";

                                errorMessages.forEach((message) => {
                                    this.showToast(message, "error");
                                });
                            }

                            this.resetOtp(false);

                            this.$nextTick(() => {
                                this.$refs.otpInputs?.[0]?.focus();
                            });

                            return;
                        }

                        console.log("OTP VERIFICATION SUCCESS", res);

                        this.successState = true;

                        this.emailVerified = true;
                        this.verifiedEmail = this.form.email;

                        clearInterval(this.timerInterval);

                        this.showToast(
                            "Email verified successfully",
                            "success"
                        );
                    }, false
                );

            } catch (err) {

                console.log("UNEXPECTED ERROR", err);

                this.otpError = "Incorrect or expired code.";

                this.showToast(
                    "Incorrect or expired code.",
                    "error"
                );

                this.resetOtp();

                this.$nextTick(() => {
                    this.$refs.otpInputs?.[0]?.focus();
                });

            }
        },

        async resendOtp() {

            try {

                this.otpError = "";

                const request = new Request(
                    "/auth/resend-email-verification/"
                );

                await request.post(
                    {
                        email: this.form.email,
                        name: this.form.firstname,
                    },
                    (res, error) => {

                        if (error) {

                            console.log("RESEND OTP ERROR", error);

                            if (error.status === "failure") {

                                this.otpError = error.message;

                                this.showToast(error.message, "error");

                                return;
                            }

                            if (error.detail) {

                                this.otpError = error.detail;

                                this.showToast(error.detail, "error");

                                return;
                            }

                            const errorMessages =
                                formatErrorMessages(error);

                            this.otpError =
                                errorMessages[0] ||
                                "Unable to resend OTP.";

                            errorMessages.forEach((message) => {
                                this.showToast(message, "error");
                            });

                            return;
                        }

                        console.log("RESEND OTP SUCCESS", res);

                        this.resetOtp();

                        this.startTimer();

                        this.showToast(
                            "OTP resent successfully",
                            "success"
                        );

                        this.$nextTick(() => {
                            this.$refs.otpInputs?.[0]?.focus();
                        });
                    }, false
                );

            } catch (err) {

                console.log("UNEXPECTED ERROR", err);

                this.otpError = "Unable to resend OTP.";

                this.showToast(
                    "Unable to resend OTP.",
                    "error"
                );
            }
        },
        formattedTimer() {
            const minutes = Math.floor(this.timer / 60);
            const seconds = this.timer % 60;

            return `${minutes}min ${seconds.toString().padStart(2, "0")}sec`;
        },
        startTimer() {

            clearInterval(this.timerInterval);

            this.timer = 90;

            this.resendDisabled = true;

            this.timerInterval = setInterval(() => {

                this.timer--;

                if (this.timer <= 0) {

                    clearInterval(this.timerInterval);

                    this.resendDisabled = false;
                }

            }, 1000);
        },

        resetOtp(clearError = true) {

            this.otp = ["", "", "", "", "", ""];

            this.verifyBtnDisabled = true;

            if (clearError) {
                this.otpError = "";
            }
        },

        onOtpInput(index, event) {

            let value = event.target.value.replace(/\D/g, "");

            this.otp[index] = value ? value[0] : "";

            if (value && index < 5) {

                this.$nextTick(() => {
                    this.$refs.otpInputs?.[index + 1]?.focus();
                });
            }

            this.checkOtpComplete();
        },
        onOtpKeydown(index, event) {

            if (
                event.key === "Backspace" &&
                !this.otp[index] &&
                index > 0
            ) {

                this.$nextTick(() => {
                    this.$refs.otpInputs?.[index - 1]?.focus();
                });
            }
        },

        onOtpPaste(event) {

            event.preventDefault();

            const pasted = (
                event.clipboardData || window.clipboardData
            )
                .getData("text")
                .replace(/\D/g, "")
                .slice(0, 6);

            pasted.split("").forEach((digit, index) => {
                this.otp[index] = digit;
            });

            this.checkOtpComplete();
        },

        checkOtpComplete() {

            const complete = this.otp.every(
                (digit) => digit !== ""
            );

            this.verifyBtnDisabled = !complete;

            // if (complete) {
            //     this.verifyOtp();
            // }
        },
        // STEP NAV
        async goToStep(n) {

            // validate current step first
            if (n > this.currentStep) {

                const valid = this.validateStep(this.currentStep);

                if (!valid) return;

                // SAVE CURRENT STEP
                const saved = await this.saveCurrentStep();

                if (!saved) return;
            }

            if (n === 4) {
                this.buildReview();
            }

            this.currentStep = n;

            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        async saveCurrentStep() {

            if (this.currentStep === 1) {
                return await this.saveStep1();
            }

            if (this.currentStep === 2) {
                return await this.saveStep2();
            }

            if (this.currentStep === 3) {
                return await this.saveStep3();
            }

            return true;
        },

        async saveStep1() {

            try {
                this.isLoading = true;

                const normalizedPhone = this.normalizePhone(this.form.phone);

                if (!normalizedPhone) {
                    this.errors.phone = "Enter a valid Nigerian phone number";
                    return;
                }

                this.form.phone = normalizedPhone;

                const normalizedWhatsapp = this.normalizePhone(this.form.whatsapp);

                if (!normalizedWhatsapp) {
                    this.errors.whatsapp = "Enter a valid Nigerian phone number";
                    return;
                }

                this.form.whatsapp = normalizedWhatsapp;

                const fd = new FormData();

                // BASIC INFO
                fd.append(
                    "caregiver_id",
                    this.caregiverId || ""
                );
                fd.append("first_name", this.form.firstname || "");
                fd.append("last_name", this.form.lastname || "");
                fd.append("dob", this.form.dob || "");
                fd.append("gender", this.form.gender || "");
                fd.append("phone", this.form.phone || "");
                fd.append("whatsapp", this.form.whatsapp || "");
                fd.append("email", this.form.email || "");
                fd.append("email_is_verified", this.emailVerified || "");
                fd.append("state", this.form.state || "");
                fd.append("relocate", this.form.relocate || false);

                // PROFILE PHOTO
                const photo = this.$refs.photoInput?.files?.[0];

                if (photo) {
                    fd.append("profile_photo", photo);
                }

                console.log("STEP 1 DATA", [...fd.entries()]);

                const savedId = this.caregiverId || sessionStorage.getItem("caregiver_id");

                let request;

                if (savedId) {

                    // UPDATE EXISTING
                    request = new Request(
                        `/onboarding/${savedId}/step-1/`
                    );

                    await request.postFormData(fd, (res, error) => {

                        if (error) {
                            console.log(error);
                            return;
                        }

                        this.caregiverId = savedId;

                        this.currentStep = 2;

                    }, false);

                } else {

                    request = new Request(
                        `/onboarding/step-1/`
                    );


                    await request.postFormData(fd, (res, error) => {
                        if (error) {
                            console.log("STEP 1 ERROR", error);

                            if (error.status === "404") {
                                this.showToast(error.message, "error");
                                return;
                            }

                            if (error.mesage) {
                                this.showToast(error.detail, "error");
                                return;
                            }

                            const errorMessages = formatErrorMessages(error);

                            errorMessages.forEach((message) => {
                                this.showToast(message, "error");
                            });

                            return;
                        }

                        console.log("STEP 1 SUCCESS", res);

                        this.showToast("Step 1 saved successfully", "success");

                        this.caregiverId = res.caregiver_id;

                        sessionStorage.setItem(
                            "caregiver_id",
                            res.caregiver_id
                        );

                        // MOVE TO NEXT STEP
                        this.currentStep = 2;
                    }, false);
                }

            } catch (err) {
                console.log("UNEXPECTED ERROR", err);
                this.showToast("Something went wrong", "error");
            } finally {
                this.isLoading = false;
            }
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        async saveStep2() {
            try {
                this.isLoading = true;

                const payload = {
                    experience_type: this.form.expType || "",
                    years: this.form.expYears || "",
                    arrangement: this.form.arrangement || "",
                    availability: this.form.availability || "",
                    bio: this.form.bio || "",
                    education_level: this.form.edu || "",
                    english_level: this.form.english || "",

                    categories: this.selectedCats || [],
                    employments: this.employments || [],
                };

                console.log("STEP 2 DATA", payload);

                const request = new Request(
                    `/onboarding/${this.caregiverId}/step-2/`
                );

                await request.patch(payload, (res, error) => {
                    if (error) {
                        console.log("STEP 2 ERROR", error);

                        if (error.status === "failure") {
                            this.showToast(error.message, "error");
                            return;
                        }

                        if (error.detail) {
                            this.showToast(error.detail, "error");
                            return;
                        }

                        const errorMessages = formatErrorMessages(error);

                        errorMessages.forEach((message) => {
                            this.showToast(message, "error");
                        });

                        return;
                    }

                    console.log("STEP 2 SUCCESS", res);

                    this.showToast("Step 2 saved successfully", "success");

                    // MOVE TO NEXT STEP
                    this.currentStep = 3;
                }, false);

            } catch (err) {
                console.log("UNEXPECTED ERROR", err);
                this.showToast("Something went wrong", "error");
            } finally {
                this.isLoading = false;
            }
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        async saveStep3() {
            try {
                this.isLoading = true;

                const fd = new FormData();

                const normalizedgPhone = this.normalizePhone(this.form.gphone);

                if (!normalizedgPhone) {
                    this.errors.gphone = "Enter a valid Nigerian phone number";
                    return;
                }

                this.form.gphone = normalizedgPhone;

                // IDENTITY
                fd.append("bvn", this.form.bvn || "");
                fd.append("nin", this.form.nin || "");
                fd.append("bvn_status", this.bvnStatus.status);
                fd.append("nin_status", this.ninStatus.status);

                // GUARANTOR
                fd.append("name", this.form.gname || "");
                fd.append("phone", this.form.gphone || "");
                fd.append("relationship", this.form.grel || "");
                fd.append("occupation", this.form.gocc || "");
                fd.append("address", this.form.g_address || "")
                fd.append("id_type", this.form.g_id_type || "")
                fd.append("id_number", this.form.g_id_num || "")


                // DOCUMENTS
                if (this.docs?.id?.file) {
                    fd.append("documents", this.docs.id.file);
                    fd.append("doc_types", "id")
                }

                if (this.docs?.cert?.file) {
                    fd.append("documents", this.docs.cert.file);
                    fd.append("doc_types", "certificate");
                }

                if (this.docs?.guarantor?.file) {
                    fd.append("documents", this.docs.guarantor.file);
                    fd.append("doc_types", "guarantor");
                }

                if (this.docs?.emp?.file) {
                    fd.append("documents", this.docs.emp.file);
                    fd.append("doc_types", "employment");
                }

                console.log("STEP 3 DATA", [...fd.entries()]);

                const request = new Request(
                    `/onboarding/${this.caregiverId}/step-3/`
                );
                return await new Promise((resolve) => {
                    request.patchFormData(fd, (res, error) => {
                        if (error) {
                            console.log("STEP 3 ERROR", error);

                            if (error.status === "failure") {
                                this.showToast(error.message, "error");
                                return;
                            }

                            if (error.detail) {
                                this.showToast(error.detail, "error");
                                return;
                            }

                            const errorMessages = formatErrorMessages(error);

                            errorMessages.forEach((message) => {
                                this.showToast(message, "error");
                            });

                            return;
                        }

                        console.log("STEP 3 SUCCESS", res);

                        this.showToast("Step 3 saved successfully", "success");

                        // MOVE TO NEXT STEP
                        this.currentStep = 4;
                        resolve(true);
                    }, false);

                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });

                });



            } catch (err) {
                console.log("UNEXPECTED ERROR", err);
                this.showToast("Something went wrong", "error");
                return false;
            } finally {
                this.isLoading = false;
            }



        },


        async fetchOnboarding() {
            try {

                this.loading = true;

                const request = new Request(
                    `/onboarding/${this.caregiverId}/`
                );

                await request.get((res, error) => {

                    if (error) {
                        console.log("FETCH ERROR", error);
                        return;
                    }

                    console.log("ONBOARDING DATA", res);


                    // RESTORE STEP
                    this.currentStep = res.current_step || 1;

                    // STEP 1
                    this.form.firstname = res.first_name || "";
                    this.form.lastname = res.last_name || "";
                    this.form.dob = res.dob || "";
                    this.form.gender = res.gender || "";
                    this.form.phone = res.phone || "";
                    this.form.whatsapp = res.whatsapp || "";
                    this.form.email = res.email || "";
                    this.emailVerified = res.emailVerified || false;
                    this.form.state = res.state || "";
                    this.form.relocate = res.relocate || "";
                    this.photoDataURL = res.profile_photo || "";

                    // STEP 2
                    this.form.expType = res.exp_type || "";
                    this.form.expYears = res.exp_years || "";
                    this.form.arrangement = res.arrangement || "";
                    this.form.availability = res.availability || "";
                    this.form.bio = res.bio || "";
                    this.form.edu = res.education || "";
                    this.form.english = res.english || "";

                    this.selectedCats = res.categories || [];
                    this.employments = res.employment?.length
                        ? res.employment
                        : [{
                            name: '',
                            phone: '',
                            email: '',
                            address: ''
                        }];

                    // STEP 3
                    this.form.bvn = res.bvn || "";
                    this.bvnStatus = {
                        loading: false,
                        status: res.bvn_status,
                        message: res.bvn_status,
                        data: null,
                    };
                    this.form.nin = res.nin || "";
                    this.ninStatus = {
                        loading: false,
                        status: res.nin_status,
                        message: res.nin_status,
                        data: null,
                    };

                    this.form.gname = res.gname || "";
                    this.form.gphone = res.gphone || "";
                    this.form.grel = res.grel || "";
                    this.form.gocc = res.gocc || "";
                    this.form.g_address = res.g_address || "";
                    this.form.g_id_type = res.g_id_type || "";
                    this.form.g_id_num = res.g_id_num || "";
                    this.restoreDocuments(res.documents);

                    this.successRef = res.care_id || "";

                    // BUILD REVIEW
                    if (this.currentStep === 4) {
                        this.buildReview();
                    }

                }, false);

            } catch (err) {

                console.log("FETCH FAILED", err);

            } finally {

                this.loading = false;
            }
        },

        validateStep(step) {

            if (step === 1) {

                this.errors = {};

                // PHOTO
                if (!this.photoDataURL) {
                    this.errors.photo = '⚠ Profile photo is required';
                }

                // FIRST NAME
                if (!this.form.firstname?.trim()) {
                    this.errors.firstname = 'First name is required';
                }

                // LAST NAME
                if (!this.form.lastname?.trim()) {
                    this.errors.lastname = 'Last name is required';
                }

                //VALIDATE EMAIL
                if (!this.emailVerified) {
                    this.errors.email = 'You have not verified your email. Verify to continue.';
                }

                if (!this.emailExist) {
                    this.errors.email = 'Email already exists';
                }

                // EMAIL
                if (!this.form.email?.trim()) {
                    this.errors.email = 'Email is required';
                }



                // PHONE
                if (!this.form.phone?.trim()) {
                    this.errors.phone = 'Phone number is required';
                }

                // DOB
                if (!this.form.dob) {

                    this.errors.dob = 'Date of birth is required';

                } else {

                    const dob = new Date(this.form.dob);
                    const today = new Date();

                    let age = today.getFullYear() - dob.getFullYear();

                    const monthDiff = today.getMonth() - dob.getMonth();

                    if (
                        monthDiff < 0 ||
                        (monthDiff === 0 && today.getDate() < dob.getDate())
                    ) {
                        age--;
                    }

                    if (age < 18) {
                        this.errors.dob = 'You must be at least 18 years old';
                    }
                }

                // WHATSAPP
                if (!this.form.whatsapp?.trim()) {
                    this.errors.whatsapp = 'Whatsapp number is required';
                }

                // GENDER
                if (!this.form.gender?.trim()) {
                    this.errors.gender = 'Gender is required';
                }

                // RELOCATE
                if (!this.form.relocate?.trim()) {
                    this.errors.relocate = 'Relocation field is required';
                }

                // STATE
                if (!this.form.state?.trim()) {
                    this.errors.state = 'State of residence is required';
                }

                // CONSENT
                if (!this.form.consent) {
                    this.errors.consent = 'You must accept the consent declaration';
                }

                // CHECK IF ERRORS EXIST
                console.log(Object.keys(this.errors).length);
                if (Object.keys(this.errors).length > 0) {

                    this.$nextTick(() => {

                        const firstError = document.querySelector('.field-error');

                        if (firstError) {
                            firstError.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }
                    });

                    return false;
                }

                return true;
            }

            if (step === 2) {

                this.errors = {
                    ...this.errors
                };

                // clear previous ref errors only
                Object.keys(this.errors).forEach(key => {
                    if (key.startsWith('ref_')) {
                        delete this.errors[key];
                    }
                });

                // CATEGORIES
                if (this.selectedCats.length === 0) {
                    this.errors.categories = 'Select at least one category';
                }
                console.log(this.selectedCats.length);

                const f = this.form;

                // EXPERIENCE TYPE
                if (!f.expType?.trim()) {
                    this.errors.expType = 'Type of experience is required';
                }

                // EXPERIENCE YEARS
                if (!f.expYears?.trim()) {
                    this.errors.expYears = 'Years of experience is required';
                }

                // ARRANGEMENT
                if (!f.arrangement?.trim()) {
                    this.errors.arrangement = 'Preferred arrangement is required';
                }

                // AVAILABILITY
                if (!f.availability?.trim()) {
                    this.errors.availability = 'Availability is required';
                }

                // BIO
                if (!f.bio?.trim()) {
                    this.errors.bio = 'Brief description is required';
                }

                // EDUCATION
                if (!f.edu) {
                    this.errors.edu = 'Education level is required';
                }

                // ENGLISH LEVEL
                if (!f.english) {
                    this.errors.english = 'English/communication level is required';
                }

                // EMPLOYMENTS
                // Only validate if user added at least one employer
                if (this.employments.length > 0) {

                    this.employments.forEach((ref, index) => {

                        if (!ref.name?.trim()) {
                            this.errors[`ref_name_${index}`] =
                                'Employer Reference name is required';
                        }

                        if (!ref.phone?.trim()) {
                            this.errors[`ref_phone_${index}`] =
                                'Employer Reference phone number is required';
                        }

                        if (!ref.address?.trim()) {
                            this.errors[`ref_address_${index}`] =
                                'Employer Reference Address is required';
                        }

                    });

                }

                // CHECK ERRORS
                if (Object.keys(this.errors).length > 0) {

                    this.$nextTick(() => {

                        const firstError = document.querySelector('.field-error');

                        if (firstError) {
                            firstError.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }
                    });

                    return false;
                }

                return true;
            }

            if (step === 3) {

                this.errors = {};

                const f = this.form;

                // BVN
                if (!f.bvn || f.bvn.length !== 11) {
                    this.errors.bvn = 'BVN must be 11 digits';
                }

                // NIN
                if (!f.nin || f.nin.length !== 11) {
                    this.errors.nin = 'NIN must be 11 digits';
                }

                //

                // ID DOCUMENT
                if (!this.docs.id.file) {
                    this.errors.id = 'Upload ID document';
                }
                if (!this.docs.cert.file) {
                    this.errors.cert = 'Upload Certificate document';
                }
                // if (!this.docs.guarantor.file) {
                //     this.errors.guarantor = 'Upload Guarantor document';
                // }
                // if (!this.docs.emp.file) {
                //     this.errors.emp = 'Upload Employment document';
                // }

                // GUARANTOR
                if (!f.gname?.trim()) {
                    this.errors.gname = 'Guarantor name is required';
                }

                if (!f.gphone?.trim()) {
                    this.errors.gphone = 'Guarantor phone is required';
                }

                if (!f.grel?.trim()) {
                    this.errors.grel = 'Relationship is required';
                }

                if (!f.gocc?.trim()) {
                    this.errors.gocc = 'Occupation is required';
                }
                if (!f.g_address?.trim()) {
                    this.errors.g_address = 'Address is required';
                }

                if (!f.g_id_type?.trim()) {
                    this.errors.id_type = 'Select Identity type';
                }
                if (!f.g_id_num?.trim()) {
                    this.errors.g_id_num = 'Identity number is required';
                }

                console.log(this.errors.length);

                // STOP IF ERRORS EXIST
                if (Object.keys(this.errors).length > 0) {

                    this.$nextTick(() => {

                        const firstError = document.querySelector('.field-error');

                        if (firstError) {
                            firstError.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }
                    });

                    return false;
                }

                return true;
            }

            return true;
        },

        // PHOTO
        previewPhoto(event) {

            const file = event.target.files[0];

            if (!file) return;

            const reader = new FileReader();

            reader.onload = (e) => {

                this.photoDataURL = e.target.result;

                delete this.errors.photo;
            };

            reader.readAsDataURL(file);
        },

        // REFERENCES
        addEmployer() {

            if (this.employments.length >= 5) {
                alert('Max 5 references.');
                return;
            }

            this.employments.push({
                name: '',
                phone: '',
                email: '',
                address: ''
            });

            delete this.errors.employments;
        },

        removeEmployer(index) {

            this.employments.splice(index, 1);

            if (this.employments.length === 0) {
                this.errors.employments =
                    'Add at least one employment reference';
            }
        },

        // CATEGORY
        toggleCat(id) {
            if (this.selectedCats.includes(id)) {
                this.selectedCats = this.selectedCats.filter(c => c !== id);
            } else {
                this.selectedCats.push(id);
            }

            delete this.errors.categories;
        },
        // DOCS
        handleDocUpload(type, event) {

            const file = event.target.files[0];

            if (!file) return;

            // max 5MB
            const maxSize = 5 * 1024 * 1024;

            if (file.size > maxSize) {

                this.errors[type] = 'File must not exceed 5MB';

                // reset input
                event.target.value = null;

                return;
            }

            const allowed = [
                'image/jpeg',
                'image/png',
                'image/jpg',
                'application/pdf'
            ];

            if (!allowed.includes(file.type)) {

                this.errors[type] = 'Only JPG, PNG or PDF files allowed';

                // reset input
                event.target.value = null;

                return;
            }

            delete this.errors[type];

            // clear previous preview
            this.docs[type].preview = null;

            // store file
            this.docs[type].file = file;
            this.docs[type].type = file.type;

            // PDF
            if (file.type === 'application/pdf') {

                this.docs[type].preview = null;

                return;
            }

            // image preview
            const reader = new FileReader();

            reader.onload = (e) => {

                this.docs[type].preview = e.target.result;
            };

            reader.readAsDataURL(file);
        },


        removeDoc(type) {

            // clear vue state
            this.docs[type] = {
                file: null,
                preview: null,
                type: null,
            };

            // clear error
            delete this.errors[type];

            if (type === 'id') {

                if (this.$refs.idCameraInput) {
                    this.$refs.idCameraInput.value = null;
                }

                if (this.$refs.idFileInput) {
                    this.$refs.idFileInput.value = null;
                }

                return;
            }

            // reset native input
            const inputMap = {
                cert: 'certInput',
                guarantor: 'gInput',
                emp: 'empInput'
            };

            const refName = inputMap[type];

            if (this.$refs[refName]) {
                this.$refs[refName].value = null;
            }
        },
        // DOWNLOAD TEMPLATE
        downloadPDF() {
            const binary = atob(this.PDF_B64);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            // Create PDF blob
            const blob = new Blob([bytes], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement("a");
            link.href = url;
            link.download = "Succour_Guarantor_Letter.pdf";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // cleanup
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
        },

        async verifyBVN() {
            try {
                this.bvnStatus.loading = true;
                this.bvnStatus.message = "";

                const payload = {
                    "id_type": "bvn",
                    "details": {
                        "bvn": this.form.bvn,
                        "name": this.form.firstname + " " + this.form.lastname,
                        "dateOfBirth": this.form.dob,
                        "mobileNo": this.form.phone
                    }
                }

                const request = new Request(
                    `/verify-identity/`
                );

                await request.post(payload, (res, error) => {

                    if (error) {
                        console.log("BVN VERIFICATION ERROR", error);

                        this.bvnStatus = {
                            loading: false,
                            status: res.bvn_status,
                            message:
                                error.message ||
                                error.detail ||
                                "BVN verification failed",
                            data: null,
                        };

                        if (error.status === "failure") {
                            this.showToast(error.message, "error");
                            return;
                        }

                        if (error.detail) {
                            this.showToast(error.detail, "error");
                            return;
                        }

                        const errorMessages = formatErrorMessages(error);

                        errorMessages.forEach((message) => {
                            this.showToast(message, "error");
                        });

                        return;
                    }

                    console.log("BVN VERIFIED", res);

                    if (res.bvn_status === "api_verified") {
                        this.bvnStatus = {
                            loading: false,
                            status: res.bvn_status,
                            message: "BVN verified successfully",
                            data: res,
                        };
                        console.log(this.bvnStatus)
                        this.showToast(
                            "BVN verified successfully",
                            "success"
                        );
                    } else {
                        this.bvnStatus = {
                            loading: false,
                            status: res.bvn_status,
                            message:
                                res.message ||
                                res.detail ||
                                "BVN verification failed",
                            data: null,
                        };

                    }

                }, false);

            } catch (err) {
                console.log("UNEXPECTED BVN ERROR", err);

                this.bvnStatus = {
                    loading: false,
                    status: "api_pending",
                    message: "Unable to verify BVN",
                    data: null,
                };

                this.showToast("Something went wrong", "error");

            } finally {
                this.bvnStatus.loading = false;
            }
        },

        async verifyNIN() {
            try {
                this.ninStatus.loading = true;
                this.ninStatus.message = "";

                const payload = {
                    id_type: "nin",
                    details: {
                        nin: this.form.nin
                    },
                };

                const request = new Request(`/verify-identity/`);

                await request.post(payload, (res, error) => {

                    if (error) {
                        console.log("NIN VERIFICATION ERROR", error);

                        this.ninStatus = {
                            loading: false,
                            status: res.nin_status,
                            message:
                                error.message ||
                                error.detail ||
                                "NIN verification failed",
                            data: null,
                        };

                        if (error.status === "failure") {
                            this.showToast(error.message, "error");
                            return;
                        }

                        if (error.detail) {
                            this.showToast(error.detail, "error");
                            return;
                        }

                        const errorMessages = formatErrorMessages(error);

                        errorMessages.forEach((message) => {
                            this.showToast(message, "error");
                        });

                        return;
                    }

                    console.log("NIN VERIFIED", res);

                    if (res.nin_status === "api_verified") {
                        this.ninStatus = {
                            loading: false,
                            status: res.nin_status,
                            message: "NIN verified successfully",
                            data: res,
                        };

                        this.showToast(
                            "NIN verified successfully",
                            "success"
                        );

                    } else {



                        this.ninStatus = {
                            loading: false,
                            status: res.nin_status,
                            message:
                                res.nin_status ||
                                res.detail ||
                                "NIN verification failed",
                            data: null,
                        };

                        this.showToast(
                            this.ninStatus.message,
                            "error"
                        );
                    }

                }, false);

            } catch (err) {

                console.log("UNEXPECTED NIN ERROR", err);

                this.ninStatus = {
                    loading: false,
                    status: "api_pending",
                    message: "Unable to verify NIN",
                    data: null,
                };

                this.showToast("Something went wrong", "error");

            } finally {

                this.ninStatus.loading = false;
            }
        },

        // REVIEW
        buildReview() {

            const maskedBVN = this.form.bvn
                ? '••••••' + this.form.bvn.slice(-5)
                : '—';

            const maskedNIN = this.form.nin
                ? '••••••' + this.form.nin.slice(-5)
                : '—';

            this.reviewHTML = `
        <div>
          <strong>${this.form.firstname} ${this.form.lastname}</strong><br/>
          DOB: ${this.form.dob}<br/>
          Phone: ${this.form.phone}<br/>
          State: ${this.form.state}<br/>
          Categories: ${this.selectedCategoryNames}<br/>
          BVN: ${maskedBVN}<br/>
          NIN: ${maskedNIN}
        </div>
    `;
        },

        async submitRegistration() {
            try {
                this.isLoading = true;

                const request = new Request(
                    `/onboarding/${this.caregiverId}/submit/`
                );

                await request.post({}, (res, error) => {

                    if (error) {
                        console.log("SUBMISSION ERROR", error);

                        if (error.status === "failure") {
                            this.showToast(error.message, "error");
                            return;
                        }

                        if (error.detail) {
                            this.showToast(error.detail, "error");
                            return;
                        }

                        const errorMessages = formatErrorMessages(error);

                        errorMessages.forEach((message) => {
                            this.showToast(message, "error");
                        });

                        return;
                    }

                    console.log("SUBMISSION SUCCESS", res);

                    this.successRef = res.care_id;

                    // clear onboarding cache
                    sessionStorage.removeItem("caregiver_id");
                    this.caregiverId = null;

                    this.goToStep(5);

                    this.showToast(
                        "Registration submitted successfully",
                        "success"
                    );
                }, false);

            } catch (err) {
                console.log("UNEXPECTED SUBMISSION ERROR", err);
                this.showToast("Something went wrong", "error");
            } finally {
                this.isLoading = false;
            }
        },

        // SUBMIT
        // submitRegistration() {
        //     const f = this.form;

        //     if (!f.firstname || !f.lastname) {
        //         alert('Missing name.');
        //         return;
        //     }

        //     const ref = 'SC-2025-' + (this.careIDCounter++);

        //     const reg = {
        //         ref,
        //         ...f,
        //         refs: this.references,
        //         cats: this.selectedCats,
        //         photo: this.photoDataURL,
        //         status: 'pending',
        //         submitted: new Date().toLocaleDateString()
        //     };

        //     this.registrations.push(reg);
        //     this.successRef = ref;

        //     this.currentStep = 5; // success state
        // }
    }
};
</script>

<style>
:root {
    --green: #17865F;
    --dkgreen: #0D5C40;
    --mdgreen: #2A9D74;
    --ltgreen: #E8F4EF;
    --bg: #D6EDE2;
    --card: #EEF7F2;
    --pink: #F59CAB;
    --amber: #FF9700;
    --white: #FFFFFF;
    --text: #1A1A1A;
    --muted: #5A7A69;
    --border: #C0DDD0;
    --red: #E74C3C;
    --red-lt: #FEE2E2;
    --orange: #F97316;
    --blue: #2563EB;
    --blue-lt: #EFF6FF;
    --teal: #0F6E56;
    --teal-dark: #085041;
    --teal-light: #E1F5EE;
    --pink-light: #FBEAF0;
    --placeholder: #A8C4B2;
    --radius: 12px;
    --shadow: 0 2px 16px rgba(15, 110, 86, 0.08);
}

html,
body {
    overflow-y: auto !important;
}
</style>

<style lang="css" scoped>
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

body.care-register {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
}

/* ── SYSTEM NAV ── */
.sys-nav {
    background: var(--dkgreen);
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid rgba(0, 0, 0, 0.15);
    position: sticky;
    top: 0;
    z-index: 200;
    overflow-x: auto;
}

.sys-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
}

.sys-logo-mark {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--pink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 13px;
    color: white;
    font-weight: 700;
}

.sys-logo-text {
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: 600;
}

.sys-logo-tm {
    font-size: 6px;
    color: rgba(255, 255, 255, 0.35);
    vertical-align: super;
}

.sys-steps {
    display: flex;
    flex: 1;
    min-width: 0;
}

.sys-step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: 0.2s;
    position: relative;
    flex-shrink: 0;
}

.sys-step:hover {
    background: rgba(255, 255, 255, 0.05);
}

.sys-step.active {
    border-bottom-color: var(--pink);
}

.sys-step.active .step-num {
    background: var(--pink);
    color: white;
}

.sys-step.active .step-label {
    color: white;
}

.sys-step.done .step-num {
    background: rgba(39, 174, 96, 0.3);
    color: #7FD4A6;
}

.sys-step.done .step-label {
    color: rgba(255, 255, 255, 0.6);
}

.doc-preview-wrap {
    margin-top: 16px;
}

.doc-preview-img {
    width: 100%;
    max-width: 220px;
    border-radius: 14px;
    border: 1px solid var(--border);
    object-fit: cover;
}

.pdf-preview {
    padding: 14px;
    border-radius: 12px;
    background: #f3f5f7;
    font-size: 13px;
    border: 1px solid var(--border);
}

.verify-success {
    font-size: 12px;
    color: green;
    margin-top: 6px;
}

.step-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: 0.2s;
}

.step-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.4);
    transition: 0.2s;
    white-space: nowrap;
}

.step-arrow {
    color: rgba(255, 255, 255, 0.15);
    font-size: 14px;
    flex-shrink: 0;
}

.sys-badge {
    margin: auto 16px auto auto;
    font-size: 9px;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
}

/* ── VIEWS ── */
.view {
    display: block;
}


/* ── SHARED ── */
.page-wrap {
    max-width: 860px;
    margin: 0 auto;
    padding: 36px 24px 80px;
}

.page-hero {
    background: var(--dkgreen);
    padding: 36px 40px 44px;
    text-align: center;
}

.hero-tag {
    display: inline-block;
    font-size: 9px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--pink);
    border: 1px solid rgba(245, 156, 171, 0.3);
    border-radius: 20px;
    padding: 4px 14px;
    margin-bottom: 12px;
}

.hero-title {
    font-family: 'Playfair Display', serif;
    font-size: 30px;
    color: white;
    font-weight: 700;
    margin-bottom: 8px;
    line-height: 1.25;
}

.hero-title em {
    color: var(--pink);
    font-style: italic;
}

.hero-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    max-width: 480px;
    margin: 0 auto;
    line-height: 1.6;
}

/* ── PROGRESS BAR ── */
.progress-bar {
    display: flex;
    gap: 0;
    margin-bottom: 28px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--border);
}

.pb-step {
    flex: 1;
    padding: 10px 14px;
    background: var(--card);
    border-right: 1px solid var(--border);
    display: flex;
    align-items: center;
    gap: 8px;
    transition: 0.3s;
}

.pb-step:last-child {
    border-right: none;
}

.pb-step.active {
    background: var(--green);
}

.pb-step.done {
    background: var(--dkgreen);
}

.pb-num {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.5);
    font-size: 9px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pb-step.active .pb-num,
.pb-step.done .pb-num {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.pb-lbl {
    font-size: 10px;
    color: var(--muted);
}

.pb-step.active .pb-lbl,
.pb-step.done .pb-lbl {
    color: white;
}

/* ── SECTION CARDS ── */
.section-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 24px 26px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.section-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}

/* ── FORM FIELDS ── */
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.form-grid-1 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.field label {
    font-size: 10px;
    font-weight: 500;
    color: var(--muted);
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.field label .req {
    color: var(--red);
    margin-left: 2px;
}

.field input,
.field select,
.field textarea {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 10px 13px;
    color: var(--text);
    outline: none;
    transition: 0.2s;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
    border-color: var(--green);
    box-shadow: 0 0 0 3px rgba(23, 134, 95, 0.1);
}

input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.7;
}

.field input.invalid {
    border-color: var(--red);
    background: #FFF5F5;
}

.field textarea {
    resize: vertical;
    min-height: 80px;
}

.field select option {
    background: white;
}

.field-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

.field-error {
    font-size: 10px;
    color: var(--red);
    margin-top: 5px;
}

/* ── PHOTO UPLOAD ── */
.photo-upload {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 14px;
}

.photo-preview {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--ltgreen);
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    flex-shrink: 0;
    overflow: hidden;
    cursor: pointer;
    transition: 0.2s;
}

.photo-preview:hover {
    border-color: var(--green);
}

.photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.photo-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.photo-upload-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.photo-upload-hint {
    font-size: 10px;
    color: var(--muted);
    margin-top: 5px;
}

.photo-required-note {
    font-size: 10px;
    color: var(--red);
    margin-top: 5px;
}

/* ── DOCUMENT UPLOAD ── */
.doc-uploaded-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    margin-top: 14px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
    transition: 0.25s ease;
}

.doc-uploaded-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.doc-thumb {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    overflow: hidden;
    flex-shrink: 0;
    background: #f3f5f7;
    display: flex;
    align-items: center;
    justify-content: center;
}

.doc-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.pdf-thumb {
    font-size: 28px;
}

.doc-meta {
    flex: 1;
    min-width: 0;
}

.doc-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .08em;
    color: #888;
    margin-bottom: 4px;
}

.doc-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.doc-remove-btn {
    border: none;
    background: #ffe9e9;
    color: #d93025;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: 0.2s ease;
}

.doc-remove-btn:hover {
    background: #ffd6d6;
}

.doc-upload-zone {
    border: 2px dashed var(--border);
    border-radius: 10px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: 0.2s;
    background: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.doc-upload-zone:hover,
.doc-upload-zone.drag {
    border-color: var(--green);
    background: var(--ltgreen);
}

.doc-icon {
    font-size: 24px;
}

.doc-upload-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
}

.doc-upload-sub {
    font-size: 10px;
    color: var(--muted);
}

.doc-upload-zone input[type=file] {
    display: none;
}

.doc-uploaded {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    border-radius: 8px;
    margin-top: 8px;
}

.doc-uploaded-name {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 500;
    flex: 1;
}

.doc-remove {
    font-size: 10px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
}

/* camera / upload dual option */
.dual-upload {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
}

.dual-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 10px;
    border-radius: 10px;
    border: 1.5px dashed var(--border);
    background: white;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: var(--dkgreen);
    transition: 0.2s;
}

.dual-btn:hover {
    border-color: var(--green);
    background: var(--ltgreen);
}

.dual-btn span {
    font-size: 22px;
}

.section-label {
    font-size: 10px;
    font-weight: 600;
    color: #6B8F78;
    letter-spacing: .07em;
    text-transform: uppercase;
    margin-bottom: 12px;
}

.id-types {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 14px;
}

.id-type-btn {
    background: #fff;
    border: 1px solid #D0E3D8;
    border-radius: 9px;
    padding: 13px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    color: #6B8F78;
    font-size: 12px;
    text-align: center;
    cursor: pointer;
    transition: .2s;
    user-select: none;
}

.id-type-btn:hover {
    border-color: #17865F;
    color: #17865F;
}

.id-type-btn.active {
    border-color: #17865F;
    color: #17865F;
    background: #E8F4EF;
}

.id-type-btn .cb {
    width: 14px;
    height: 14px;
    border: 1.5px solid currentColor;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.id-type-btn.active .cb {
    background: #17865F;
    border-color: #17865F;
}

.id-type-btn.active .cb::after {
    content: '✓';
    font-size: 9px;
    color: #fff;
    line-height: 1;
}

.word-count {
    font-size: 12px;
    color: #777;
    margin-top: 4px;
    text-align: right;
}

.field-label {
    font-size: 10px;
    font-weight: 600;
    color: #6B8F78;
    letter-spacing: .07em;
    text-transform: uppercase;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.divider {
    border: none;
    border-top: 1px solid #E2EDE7;
    margin: 18px 0;
}

/* ── REFERENCE ROWS ── */
.ref-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 10px;
    align-items: end;
    margin-bottom: 10px;
    background: white;
    padding: 12px;
    border-radius: 9px;
    border: 1px solid var(--border);
}

.ref-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 4px;
}

.ref-remove-btn {
    width: 30px;
    height: 36px;
    margin-left: auto;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: white;
    color: var(--red);
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.2s;
    align-self: flex-end;
}

.ref-remove-btn:hover {
    background: var(--red-lt);
}

.add-ref-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-ref-btn:hover {
    background: var(--card);
}

/* ── EMPLOYMENT HISTORY ROWS ── */
.emp-history-row {
    background: white;
    padding: 14px 16px;
    border-radius: 9px;
    border: 1px solid var(--border);
    margin-bottom: 10px;
}

.emp-row-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.emp-row-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
}

.emp-row-remove {
    font-size: 11px;
    color: var(--red);
    cursor: pointer;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 5px;
    border: 1px solid var(--red);
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    transition: 0.2s;
}

.emp-row-remove:hover {
    background: var(--red-lt);
}

.add-emp-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px dashed var(--green);
    background: var(--ltgreen);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
    margin-top: 4px;
}

.add-emp-btn:hover {
    background: var(--card);
}

/* ── CATEGORY TOGGLES ── */
.cat-grid {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.cat-toggle {
    padding: 8px 16px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: white;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
    cursor: pointer;
    transition: 0.2s;
    user-select: none;
}

.cat-toggle.selected {
    background: var(--green);
    border-color: var(--green);
    color: white;
}

/* ── CONSENT BOX ── */
.consent-box {
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 16px;
}

.consent-inner {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.consent-inner input[type=checkbox] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: var(--green);
    cursor: pointer;
}

.consent-text {
    font-size: 12px;
    color: var(--text);
    line-height: 1.6;
}

.consent-text strong {
    color: var(--dkgreen);
}

/* ── SUBMIT BTN ── */
.submit-btn {
    width: 100%;
    padding: 15px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 8px;
}

.submit-btn:hover {
    background: var(--mdgreen);
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ── SUCCESS STATE ── */
.success-state {
    text-align: center;
    padding: 48px 24px;
}

.success-state.show {
    display: block;
    animation: fadeUp 0.4s ease;
}

.success-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.success-title {
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    color: var(--dkgreen);
    margin-bottom: 8px;
}

.success-sub {
    font-size: 13px;
    color: var(--muted);
    max-width: 400px;
    margin: 0 auto 24px;
    line-height: 1.6;
}

.success-ref {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: var(--green);
    background: var(--ltgreen);
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid var(--border);
    display: inline-block;
}

/* ── OPS QUEUE ── */
.ops-banner {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    border-radius: 10px;
    padding: 12px 18px;
    margin-bottom: 20px;
    font-size: 11px;
    color: #92400E;
    display: flex;
    align-items: center;
    gap: 8px;
}

.queue-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
}

.queue-table th {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    padding: 10px 12px;
    text-align: left;
    border-bottom: 2px solid var(--border);
}

.queue-table td {
    font-size: 12px;
    color: var(--text);
    padding: 12px 12px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
}

.queue-table tr:hover td {
    background: var(--ltgreen);
}

.status-pill {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 10px;
    border-radius: 20px;
}

.status-pill.pending {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.25);
}

.status-pill.in-progress {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.status-pill.complete {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.review-btn {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: white;
    color: var(--dkgreen);
    cursor: pointer;
    transition: 0.2s;
}

.review-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

/* ── OPS VERIFICATION PANEL ── */
.ops-profile {
    background: white;
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px 22px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.ops-av {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ops-av img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ops-profile-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--dkgreen);
}

.ops-profile-meta {
    font-size: 11px;
    color: var(--muted);
    margin-top: 3px;
}

.ops-profile-ref {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

/* API badge */
.api-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 9px;
    border-radius: 20px;
    margin-left: 8px;
}

.api-badge.auto {
    background: rgba(37, 99, 235, 0.08);
    color: #1D4ED8;
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.api-badge.manual {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

/* signals */
.sig-block {
    background: white;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 14px;
}

.sig-block-title {
    font-size: 9px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.sig-block-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.sig-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid var(--border);
}

.sig-row:last-child {
    border: none;
    padding-bottom: 0;
}

.sig-left {
    flex: 1;
}

.sig-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--text);
}

.sig-sub {
    font-size: 10px;
    color: var(--muted);
    margin-top: 2px;
}

.sig-doc {
    font-size: 10px;
    color: var(--green);
    text-decoration: underline;
    cursor: pointer;
    margin-top: 2px;
}

.sig-select {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 6px 10px;
    color: var(--text);
    outline: none;
    min-width: 200px;
    cursor: pointer;
}

.sig-select:focus {
    border-color: var(--green);
}

.sig-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

/* reputation counters */
.rep-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-top: 8px;
}

.rep-counter {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    text-align: center;
}

.rep-counter-label {
    font-size: 9px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
}

.rep-ctrl {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.rev-btn {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 1px solid var(--border);
    background: white;
    color: var(--green);
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.15s;
}

.rev-btn:hover {
    background: var(--ltgreen);
    border-color: var(--green);
}

.rev-count {
    font-size: 16px;
    font-weight: 700;
    color: var(--dkgreen);
    min-width: 22px;
    text-align: center;
}

/* NPF upload */
.npf-upload {
    margin-top: 10px;
    display: none;
}

.npf-upload.show {
    display: block;
}

/* compute */
.compute-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--green);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 4px;
}

.compute-btn:hover {
    background: var(--mdgreen);
}

/* DNE banner */
.dne-banner {
    background: #FEE2E2;
    border: 2px solid var(--red);
    border-radius: 12px;
    padding: 20px 22px;
    margin-top: 14px;
    display: none;
    animation: fadeUp 0.35s ease;
}

.dne-banner.show {
    display: block;
}

.dne-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: var(--red);
    font-weight: 700;
    margin-bottom: 6px;
}

.dne-sub {
    font-size: 13px;
    color: #7F1D1D;
    line-height: 1.6;
}

/* score result */
.score-result {
    display: none;
    background: var(--dkgreen);
    border-radius: 14px;
    padding: 24px 26px;
    margin-top: 14px;
}

.score-result.show {
    display: block;
    animation: fadeUp 0.35s ease;
}

.sr-top {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
}

.sr-num {
    font-family: 'Playfair Display', serif;
    font-size: 56px;
    color: white;
    line-height: 1;
    font-weight: 700;
}

.sr-denom {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.35);
    margin-top: 4px;
}

.sr-tier {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 16px;
    border-radius: 20px;
    display: inline-block;
    margin-bottom: 6px;
}

.sr-tier.unverified {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.sr-tier.bronze {
    background: rgba(255, 151, 0, 0.2);
    color: var(--amber);
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.sr-tier.silver {
    background: rgba(200, 230, 215, 0.2);
    color: #A7D4BE;
    border: 1px solid rgba(200, 230, 215, 0.3);
}

.sr-tier.gold {
    background: rgba(255, 215, 0, 0.15);
    color: #FFD700;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.sr-deploy {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
}

.sr-checklist {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.sr-check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.05);
}

.sr-ck-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    flex-shrink: 0;
}

.sr-ck-icon.ok {
    background: rgba(39, 174, 96, 0.25);
    color: #7FD4A6;
}

.sr-ck-icon.fail {
    background: rgba(231, 76, 60, 0.2);
    color: #FCA5A5;
}

.sr-ck-lbl {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
}

.issue-btn {
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
    margin-top: 12px;
}

.issue-btn:hover {
    background: #e08a99;
}

/* ── EMPLOYER VIEW (standard) ── */
.ev-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 480px;
    margin: 0 auto;
}

.ev-stripe {
    height: 5px;
    background: linear-gradient(90deg, var(--green), #2CC89A, var(--pink));
}

.ev-body {
    padding: 24px 26px;
}

.ev-hdr {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #EEF4F1;
}

.ev-photo {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--green), #2CC89A);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 20px;
    color: white;
    font-weight: 700;
    flex-shrink: 0;
    overflow: hidden;
}

.ev-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ev-name {
    font-family: 'Playfair Display', serif;
    font-size: 17px;
    color: var(--text);
    font-weight: 700;
}

.ev-sc {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--muted);
    margin-top: 3px;
}

.ev-tbadge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
}

.ev-tbadge.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.25);
}

.ev-tbadge.silver {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-tbadge.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.2);
}

.ev-score-block {
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--ltgreen);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 18px;
    border: 1px solid rgba(23, 134, 95, 0.12);
}

.ev-score-num {
    font-family: 'Playfair Display', serif;
    font-size: 46px;
    color: var(--dkgreen);
    line-height: 1;
    font-weight: 700;
}

.ev-score-denom {
    font-size: 12px;
    color: var(--muted);
}

.ev-score-level {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 3px 12px;
    border-radius: 20px;
    margin-top: 4px;
}

.ev-score-level.gold {
    background: rgba(255, 215, 0, 0.1);
    color: #B8860B;
    border: 1px solid rgba(255, 215, 0, 0.2);
}

.ev-score-level.silver {
    background: rgba(23, 134, 95, 0.1);
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.ev-score-level.bronze {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.15);
}

.ev-cats-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 7px;
}

.ev-cats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.ev-cat {
    font-size: 10px;
    color: var(--dkgreen);
    font-weight: 500;
    background: var(--ltgreen);
    border: 1px solid var(--border);
    padding: 4px 10px;
    border-radius: 20px;
}

.ev-br-lbl {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 10px;
}

.ev-row-item {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.ev-rl {
    font-size: 11px;
    color: var(--text);
    width: 100px;
    flex-shrink: 0;
}

.ev-bar-track {
    flex: 1;
    height: 6px;
    background: #EEF4F1;
    border-radius: 6px;
    overflow: hidden;
}

.ev-bar-fill {
    height: 100%;
    border-radius: 6px;
}

.ev-bar-fill.excellent {
    background: var(--green);
    width: 92%;
}

.ev-bar-fill.good {
    background: var(--amber);
    width: 70%;
}

.ev-bar-fill.average {
    background: #F0A05A;
    width: 48%;
}

.ev-bar-fill.poor {
    background: var(--red);
    width: 24%;
}

.ev-rval {
    font-size: 10px;
    font-weight: 600;
    width: 58px;
    text-align: right;
}

.ev-rval.excellent {
    color: var(--green);
}

.ev-rval.good {
    color: #B36800;
}

.ev-rval.average {
    color: #E07020;
}

.ev-rval.poor {
    color: var(--red);
}

.ev-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 14px;
    background: #E8F7F0;
    border-radius: 10px;
    border: 1px solid rgba(23, 134, 95, 0.2);
    margin: 14px 0;
}

.ev-sdot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green);
    flex-shrink: 0;
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1
    }

    50% {
        opacity: 0.5
    }
}

.ev-stext {
    font-size: 12px;
    color: var(--dkgreen);
    font-weight: 600;
}

.ev-sdate {
    font-size: 10px;
    color: var(--muted);
    margin-left: auto;
}

.ev-qr-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 14px;
    border-top: 1px solid #EEF4F1;
}

.ev-qr-box {
    width: 60px;
    height: 60px;
    background: var(--ltgreen);
    border-radius: 8px;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ev-share-btns {
    display: flex;
    gap: 8px;
}

.ev-share-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.2s;
}

.ev-share-btn.wa {
    background: #25D366;
    color: white;
}

.ev-share-btn.wa:hover {
    background: #1DA851;
}

.ev-share-btn.copy {
    background: var(--ltgreen);
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.ev-share-btn.copy:hover {
    background: #D0EBE0;
}

.ev-footer-lnk {
    font-size: 9px;
    color: var(--muted);
    text-align: center;
    margin-top: 12px;
}

.ev-footer-lnk a {
    color: var(--green);
    font-weight: 600;
    text-decoration: none;
}

.phone-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.same-check {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    cursor: pointer;
}

/* ── DETAILED EMPLOYER VIEW (payment gated) ── */
.pay-gate {
    background: var(--dkgreen);
    border-radius: 16px;
    overflow: hidden;
    max-width: 560px;
    margin: 0 auto 20px;
}

.pay-gate-body {
    padding: 32px;
}

.pay-gate-icon {
    font-size: 36px;
    margin-bottom: 12px;
}

.pay-gate-title {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    margin-bottom: 8px;
}

.pay-gate-sub {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.6;
    margin-bottom: 24px;
}

.pay-features {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
}

.pay-feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
}

.pay-feature-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--pink);
    flex-shrink: 0;
}

.pay-price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 20px;
}

.pay-amount {
    font-family: 'Playfair Display', serif;
    font-size: 32px;
    color: white;
    font-weight: 700;
}

.pay-period {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);
}

.pay-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: var(--pink);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.5px;
    transition: 0.2s;
}

.pay-btn:hover {
    background: #e08a99;
}

.pay-note {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.3);
    text-align: center;
    margin-top: 10px;
}

/* detailed report */
.det-report {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    max-width: 600px;
    margin: 0 auto;
    display: none;
    animation: fadeUp 0.4s ease;
}

.det-report.show {
    display: block;
}

.det-header {
    background: var(--dkgreen);
    padding: 24px 26px;
    display: flex;
    align-items: center;
    gap: 14px;
}

.det-photo {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    color: white;
    flex-shrink: 0;
    overflow: hidden;
}

.det-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.det-title {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: white;
    font-weight: 700;
}

.det-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 3px;
}

.det-badge {
    margin-left: auto;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
}

.det-body {
    padding: 24px 26px;
}

.det-section {
    margin-bottom: 22px;
}

.det-section-title {
    font-size: 8px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.det-section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
}

.det-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid var(--border);
    font-size: 12px;
}

.det-row:last-child {
    border: none;
}

.det-key {
    color: var(--muted);
}

.det-val {
    font-weight: 500;
    color: var(--text);
}

.det-status {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 20px;
}

.det-status.verified {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.not-verified {
    background: #FFFBEB;
    color: #92400E;
    border: 1px solid rgba(255, 151, 0, 0.3);
}

.det-status.forged {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.clear {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.record {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-status.positive {
    background: #E8F7F0;
    color: var(--green);
    border: 1px solid rgba(23, 134, 95, 0.2);
}

.det-status.negative {
    background: var(--red-lt);
    color: var(--red);
    border: 1px solid rgba(231, 76, 60, 0.3);
}

.det-warning {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 11px;
    color: #7F1D1D;
    margin-top: 12px;
}

/* ── CARE ID ── */
.cid-cat {
    font-size: 8px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 20px;
}

.cid-cat-dark {
    color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.cid-cat-light {
    color: var(--dkgreen);
    background: var(--ltgreen);
    border: 1px solid var(--border);
}

.cid-share-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 9px 10px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    transition: 0.18s;
}

.cid-share-btn.wa {
    background: #25D366;
    color: white;
}

.cid-share-btn.wa:hover {
    background: #1DA851;
}

.cid-share-btn.copy {
    background: white;
    color: var(--dkgreen);
    border: 1px solid var(--border);
}

.cid-share-btn.copy:hover {
    background: var(--ltgreen);
}

.upload-hint {
    background: #E8F4EF;
    border: 1px solid #C0DDD0;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-top: 10px;
}

.upload-hint i {
    color: #17865F;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
}

.upload-hint span {
    font-size: 12px;
    color: #17865F;
    line-height: 1.5;
}

.dl-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: #fff;
    border: 1px solid #D0E3D8;
    border-radius: 9px;
    color: #17865F;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Open Sans', sans-serif;
    cursor: pointer;
    transition: .2s;
    margin-top: 10px;
}

.dl-btn:hover {
    background: #E8F4EF;
    border-color: #17865F;
}

.dl-btn i {
    font-size: 16px;
}

.format-hint {
    font-size: 10px;
    color: #9CB8A6;
    margin-top: 5px;
}

/* ── UTILITIES ── */
@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.notice {
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.notice.info {
    background: rgba(37, 99, 235, 0.06);
    border: 1px solid rgba(37, 99, 235, 0.15);
    color: #1D4ED8;
}

.notice.warn {
    background: #FFFBEB;
    border: 1px solid rgba(255, 151, 0, 0.3);
    color: #92400E;
}

.notice.danger {
    background: var(--red-lt);
    border: 1px solid rgba(231, 76, 60, 0.3);
    color: #7F1D1D;
}

/* OTP MODAL */
.overlay {
    position: fixed;
    inset: 0;
    background: rgba(10, 30, 20, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 200;
    opacity: 0;
    pointer-events: none;
    transition: opacity .25s;
}

.overlay.show {
    opacity: 1;
    pointer-events: all;
}

.modal {
    background: #fff;
    width: 100%;
    max-width: 460px;
    border-radius: 24px 24px 0 0;
    padding: 0;
    box-shadow: 0 -8px 40px rgba(15, 110, 86, 0.18);
    transform: translateY(100%);
    transition: transform .32s cubic-bezier(0.32, 0.72, 0, 1);
    overflow: hidden;
    max-height: 95vh;
    overflow-y: auto;
}

.overlay.show .modal {
    transform: translateY(0);
}

.modal-handle {
    display: flex;
    justify-content: center;
    padding: 12px 0 4px;
}

.modal-handle-bar {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: var(--border);
}

.modal-topbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 4px 16px 0;
}

.modal-close-btn {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: none;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background .15s;
    -webkit-tap-highlight-color: transparent;
}

.modal-close-btn:hover {
    background: var(--border);
}

.modal-close-btn svg {
    width: 14px;
    height: 14px;
    stroke: var(--muted);
    fill: none;
    stroke-width: 2.5;
    stroke-linecap: round;
}

.modal-body {
    padding: 12px 24px 32px;
}

@supports(padding-bottom:env(safe-area-inset-bottom)) {
    .modal-body {
        padding-bottom: calc(32px + env(safe-area-inset-bottom));
    }
}

.modal-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: var(--teal-light);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
}

.modal-icon svg {
    width: 24px;
    height: 24px;
    stroke: var(--teal);
    fill: none;
    stroke-width: 1.8;
}


.modal-title {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    text-align: center;
    color: var(--text);
    margin-bottom: 6px;
    line-height: 1.2;
}

.modal-sub {
    font-size: 13px;
    color: var(--muted);
    text-align: center;
    line-height: 1.6;
    margin-bottom: 22px;
}

.modal-sub strong {
    color: var(--teal);
    font-weight: 600;
    word-break: break-all;
}

/* EMAIL VERIFIED */
.email-verified {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #3BAF7A;
    font-weight: 600;
    margin-top: 4px;
}

.email-verified svg {
    width: 13px;
    height: 13px;
    stroke: #3BAF7A;
    fill: none;
    stroke-width: 2.5;
}

.otp-row {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-bottom: 35px;
}

.otp-box {
    flex: 1;
    max-width: 52px;
    height: 56px;
    border: 2px solid var(--border);
    border-radius: 10px;
    text-align: center;
    font-size: 22px;
    font-weight: 700;
    font-family: 'DM Serif Display', serif;
    color: var(--text);
    outline: none;
    transition: border-color .15s, box-shadow .15s;
    background: #fff;
    -webkit-appearance: none;
    min-height: 44px;
}

.otp-box:focus {
    border-color: var(--teal);
    box-shadow: 0 0 0 3px rgba(15, 110, 86, 0.12);
}

.otp-box.filled {
    border-color: var(--teal);
    background: var(--teal-light);
}

.otp-box.error-box {
    border-color: var(--pink);
    animation: shake .35s ease;
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0)
    }

    25% {
        transform: translateX(-6px)
    }

    75% {
        transform: translateX(6px)
    }
}

.otp-error {
    font-size: 12px;
    color: #D4537E;
    text-align: center;
    margin-bottom: 10px;
    min-height: 16px;
    font-weight: 500;
}

.verify-btn {
    width: 100%;
    background: var(--teal);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 14px;
    font-size: 15px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: background .15s;
    min-height: 50px;
    -webkit-tap-highlight-color: transparent;
}

.verify-btn:hover {
    background: var(--teal-dark);
}

.verify-btn:disabled {
    background: var(--border);
    color: var(--placeholder);
    cursor: not-allowed;
}

.resend-row {
    text-align: center;
    margin-top: 14px;
    font-size: 12px;
    color: var(--muted);
    line-height: 1.8;
}

.resend-btn {
    background: none;
    border: none;
    color: var(--teal);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
}

.resend-btn:disabled {
    color: var(--placeholder);
    cursor: not-allowed;
}

.demo-hint {
    margin-top: 12px;
    text-align: center;
    font-size: 11px;
    color: var(--placeholder);
}

.modal-success {
    text-align: center;
}

.success-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--teal-light);
    border: 3px solid var(--teal);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
}

.success-icon svg {
    width: 28px;
    height: 28px;
    stroke: var(--teal);
    fill: none;
    stroke-width: 2.5;
}

.success-title {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    color: var(--text);
    margin-bottom: 6px;
}

.section-hint {
    color: var(--red);
    font-size: 11px;
    margin-left: 6px;
}

.success-sub {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.6;
    margin-bottom: 24px;
}

.continue-btn {
    width: 100%;
    background: var(--teal);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 14px;
    font-size: 15px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    min-height: 50px;
    -webkit-tap-highlight-color: transparent;
}

.continue-btn:hover {
    background: var(--teal-dark);
}

/* RESPONSIVE */
@media(min-width:600px) {
    .nav-url {
        display: block;
    }

    .overlay {
        align-items: center;
    }

    .modal {
        border-radius: 20px;
        transform: translateY(20px) scale(0.97);
    }

    .overlay.show .modal {
        transform: translateY(0) scale(1);
    }

    .modal-handle {
        display: none;
    }

    .modal-body {
        padding: 12px 32px 36px;
    }
}

@media(min-width:480px) and (max-width:719px) {
    .main {
        padding: 16px 14px 96px;
    }
}

@media (max-width: 768px) {
    .ref-row {
        grid-template-columns: 1fr;
        /* stack items */
        gap: 8px;
        align-items: stretch;
    }

    .dl-btn {
        font-size: 11px;
    }

    .progress-bar {
        margin-bottom: 16px;
        border-radius: 8px;
    }

    .pb-step {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid var(--border);
    }

    .pb-lbl {
        font-size: 8.5px;
    }


}

@media(max-width:479px) {
    .section-hint {
        font-size: 8px;
        margin-left: 0;
    }

    .nav-name {
        font-size: 13px;
    }

    .steps {
        margin-left: 10px;
    }

    .step {
        padding: 0 8px;
        font-size: 10px;
        gap: 4px;
    }

    .step-sep {
        display: none;
    }

    .mobile-step-bar {
        display: flex;
    }

    .main {
        padding: 14px 12px 90px;
    }

    .card-header {
        padding: 12px 14px 10px;
    }

    .card-body {
        padding: 14px;
    }

    .form-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .form-group.full {
        grid-column: 1;
    }

    .photo-circle {
        width: 60px;
        height: 60px;
    }

    .upload-photo-btn {
        width: 100%;
        justify-content: center;
    }

    .id-types {
        grid-template-columns: repeat(2, 1fr);
    }

    .otp-row {
        gap: 6px;
    }

    .otp-box {
        height: 52px;
        width: 15px;
        font-size: 20px;
        max-width: none;
    }

    .modal-body {
        padding: 8px 16px 28px;
    }

    .modal-title {
        font-size: 20px;
    }
}

@media(max-width:359px) {
    .otp-row {
        gap: 4px;
    }

    .otp-box {
        height: 48px;
        width: 15px;
        font-size: 18px;
        border-radius: 8px;
    }

    .step .num {
        display: none;
    }
}
</style>